import React, { useEffect, useState, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import StripeCardForm from "../components/StripeCardForm";
import BlogTab from "../components/admin/BlogTab";
import {
  Shield, Calendar, Mail, CreditCard, Users, DollarSign, Check, X, Save, Edit,
  ChevronLeft, ChevronRight, FileText, Download, Trash2, Gavel, Send, MailCheck, Search, Trophy, Inbox,
  TrendingUp, AlertTriangle, Sparkles, Activity, Handshake, ExternalLink, Plus, Banknote, FileSignature, Newspaper, BookOpen, Eye, Lock,
} from "lucide-react";

function _relativeTime(iso) {
  if (!iso) return "";
  const diff = Date.now() - new Date(iso).getTime();
  const s = Math.round(diff / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.round(m / 60);
  if (h < 48) return `${h}h ago`;
  const d = Math.round(h / 24);
  return `${d}d ago`;
}


const REFERRAL_SOURCES = [
  "Facebook", "Instagram", "TikTok", "Google Search", "Referral — Client",
  "Referral — Partner", "Walk-in / In Person", "Ebook", "Event", "Other",
];

const TABS = [
  { id: "overview", label: "Overview", icon: Shield },
  { id: "leads", label: "Leads", icon: Inbox },
  { id: "bookings", label: "Bookings", icon: Calendar },
  { id: "contacts", label: "Contacts", icon: Mail },
  { id: "payments", label: "Payments", icon: CreditCard },
  { id: "users", label: "Users", icon: Users },
  { id: "documents", label: "Documents", icon: FileText },
  { id: "disputes", label: "Disputes", icon: Gavel },
  { id: "marketplace", label: "Marketplace", icon: Handshake },
  { id: "contracts", label: "Contracts", icon: FileSignature },
  { id: "blog", label: "Blog", icon: Newspaper },
  { id: "mail_queue", label: "Mail Queue", icon: Send },
];

const DISPUTE_STATUS_META = {
  drafted:       { label: "Drafted",       icon: FileText,  cls: "bg-white/10 text-white/70" },
  sent:          { label: "Sent",          icon: Send,      cls: "bg-blue-500/15 text-blue-300" },
  acknowledged:  { label: "Acknowledged",  icon: MailCheck, cls: "bg-cyan-500/15 text-cyan-300" },
  investigating: { label: "Investigating", icon: Search,    cls: "bg-amber-500/15 text-amber-300" },
  resolved:      { label: "Resolved",      icon: Trophy,    cls: "bg-green-500/15 text-green-300" },
};

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

function bookingStatusBadgeClass(status) {
  if (status === "completed") return "bg-green-500/15 text-green-400";
  if (status === "cancelled") return "bg-red-500/15 text-red-400";
  return "bg-[var(--if-gold)]/15 text-[var(--if-gold)]";
}

function paymentStatusBadgeClass(status) {
  if (status === "paid") return "bg-green-500/15 text-green-400";
  if (status === "pending") return "bg-[var(--if-gold)]/15 text-[var(--if-gold)]";
  return "bg-red-500/15 text-red-400";
}

function userRoleBadgeClass(role) {
  return role === "admin"
    ? "bg-[var(--if-gold)]/15 text-[var(--if-gold)]"
    : "bg-white/10 text-white/70";
}

function StatCard({ label, value, icon: Icon }) {
  return (
    <div className="card-dark p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="font-mono-label text-white/50">{label}</span>
        <Icon className="w-4 h-4 text-[var(--if-gold)]" />
      </div>
      <div className="font-display text-3xl text-white">{value}</div>
    </div>
  );
}

function usePagination(endpoint, defaultLimit = 25) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [limit, setLimit] = useState(defaultLimit);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`${endpoint}?limit=${limit}&offset=${offset}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } finally {
      setLoading(false);
    }
  }, [endpoint, limit, offset]);

  useEffect(() => { load(); }, [load]);

  const page = Math.floor(offset / limit) + 1;
  const totalPages = Math.max(1, Math.ceil(total / limit));
  const next = () => setOffset(Math.min(offset + limit, (totalPages - 1) * limit));
  const prev = () => setOffset(Math.max(0, offset - limit));
  const setSize = (n) => { setLimit(n); setOffset(0); };

  return { items, total, limit, offset, loading, page, totalPages, next, prev, setSize, reload: load };
}

function PageControls({ page, totalPages, total, limit, offset, setSize, next, prev, label }) {
  if (total === 0) return null;
  const start = total === 0 ? 0 : offset + 1;
  const end = Math.min(offset + limit, total);
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-white/10" data-testid="pagination-controls">
      <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
        Showing <strong className="text-white">{start}–{end}</strong> of <strong className="text-white">{total}</strong> {label}
      </div>
      <div className="flex items-center gap-3">
        <select
          value={limit}
          onChange={(e) => setSize(Number(e.target.value))}
          className="bg-white/5 border border-white/10 rounded-lg px-3 py-1.5 text-xs text-white/80 focus:border-[var(--if-gold)]/40 outline-none"
          data-testid="page-size-select"
        >
          {PAGE_SIZE_OPTIONS.map((n) => (
            <option key={n} value={n} className="bg-white">{n} / page</option>
          ))}
        </select>
        <div className="flex items-center gap-1">
          <button
            onClick={prev}
            disabled={page === 1}
            className="p-2 rounded-lg border border-white/10 text-white/70 hover:border-[var(--if-gold)]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="page-prev"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs text-white/70 px-2 min-w-[70px] text-center" style={{ fontFamily: "Inter, sans-serif" }}>
            Page {page} / {totalPages}
          </span>
          <button
            onClick={next}
            disabled={page === totalPages}
            className="p-2 rounded-lg border border-white/10 text-white/70 hover:border-[var(--if-gold)]/40 disabled:opacity-30 disabled:cursor-not-allowed transition-all"
            data-testid="page-next"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

function LeadsTab() {
  const p = usePagination("/admin/leads", 25);
  const remove = async (l) => {
    if (!window.confirm(`Delete lead from ${l.email}?`)) return;
    try {
      await api.delete(`/admin/leads/${l.id}`);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };
  const exportCsv = async () => {
    try {
      const res = await api.get("/admin/leads/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `infantry-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e?.response?.data?.detail || "Export failed."); }
  };
  return (
    <div className="flex flex-col gap-3">
      <div className="flex justify-end mb-1">
        <button
          onClick={exportCsv}
          disabled={p.total === 0}
          className="btn-outline !py-2 !px-4 !text-[11px] disabled:opacity-40 disabled:cursor-not-allowed"
          data-testid="leads-export-csv"
        >
          <Download className="w-3.5 h-3.5" /> Export CSV
        </button>
      </div>
      {p.items.map((l) => (
        <div key={l.id} className="card-dark p-5" data-testid={`admin-lead-${l.id}`}>
          <div className="flex flex-wrap justify-between items-start gap-4 mb-2">
            <div className="min-w-0">
              <div className="font-display text-lg text-white">{l.name || "—"}</div>
              <div className="text-xs text-white/55 mt-0.5 break-all" style={{ fontFamily: "Inter, sans-serif" }}>
                <a href={`mailto:${l.email}`} className="text-[var(--if-gold)] underline">{l.email}</a>
                {l.phone && <> · <a href={`tel:${l.phone}`} className="text-[var(--if-gold)] underline" data-testid={`lead-phone-${l.id}`}>{l.phone}</a></>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <span className="text-xs text-white/40">{new Date(l.created_at).toLocaleString()}</span>
              <button
                onClick={() => remove(l)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all"
                data-testid={`delete-lead-${l.id}`}
              >
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
            <span className="px-2.5 py-1 rounded-full bg-[var(--if-gold)]/15 text-[var(--if-gold)]">
              Goal: {l.goal}
            </span>
            {l.source && (
              <span className="px-2.5 py-1 rounded-full bg-white/5 text-white/55 capitalize">
                {String(l.source).replace(/_/g, " ")}
              </span>
            )}
          </div>
        </div>
      ))}
      {p.items.length === 0 && !p.loading && (
        <p className="text-sm text-white/50 text-center py-8">No leads yet. The site survey pop-up will populate this.</p>
      )}
      <PageControls {...p} label="leads" />
    </div>
  );
}

function BookingsTab() {
  const p = usePagination("/admin/bookings", 25);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({});

  const startEdit = (b) => {
    setEditing(b.id);
    setForm({ status: b.status, meeting_link: b.meeting_link, admin_notes: b.admin_notes || "" });
  };
  const save = async () => {
    await api.patch(`/admin/bookings/${editing}`, form);
    setEditing(null);
    await p.reload();
  };

  return (
    <div className="flex flex-col gap-3">
      {p.items.map((b) => {
        const isEdit = editing === b.id;
        return (
          <div key={b.id} className="card-dark p-5" data-testid={`admin-booking-${b.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-4 mb-3">
              <div>
                <div className="font-display text-lg text-white">{b.first_name} {b.last_name}</div>
                <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
                  {b.service} · {b.date} {b.time} CT · <span className="capitalize">{b.meeting_type.replace("_", " ")}</span>
                </div>
                <div className="text-xs text-white/55 mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
                  {b.email} · {b.phone}
                </div>
              </div>
              {isEdit ? (
                <div className="flex gap-2">
                  <button onClick={save} className="btn-gold !py-2 !px-4 !text-[11px]"><Save className="w-3.5 h-3.5" /> Save</button>
                  <button onClick={() => setEditing(null)} className="btn-outline !py-2 !px-4 !text-[11px]"><X className="w-3.5 h-3.5" /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => startEdit(b)} className="btn-outline !py-2 !px-4 !text-[11px]" data-testid={`edit-booking-${b.id}`}>
                    <Edit className="w-3.5 h-3.5" /> Edit
                  </button>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Delete booking for ${b.first_name} ${b.last_name}?`)) return;
                      try {
                        await api.delete(`/admin/bookings/${b.id}`);
                        await p.reload();
                      } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
                    }}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all"
                    data-testid={`delete-booking-${b.id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Delete
                  </button>
                </div>
              )}
            </div>
            {b.goal && <p className="text-sm text-white/70 italic mb-3" style={{ fontFamily: "Inter, sans-serif" }}>"{b.goal}"</p>}

            {isEdit ? (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="font-mono-label text-white/50 block mb-1">Status</label>
                  <select className="if-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="scheduled">Scheduled</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                  </select>
                </div>
                <div>
                  <label className="font-mono-label text-white/50 block mb-1">Meeting link</label>
                  <input className="if-input" placeholder="Paste real Zoom / Meet link" value={form.meeting_link}
                    onChange={(e) => setForm({ ...form, meeting_link: e.target.value })} data-testid={`edit-link-${b.id}`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono-label text-white/50 block mb-1">Admin notes</label>
                  <textarea className="if-input" rows={2} value={form.admin_notes}
                    onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
                </div>
              </div>
            ) : (
              <div className="flex flex-wrap gap-3 items-center text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
                <span className={`px-2.5 py-1 rounded-full capitalize ${bookingStatusBadgeClass(b.status)}`}>{b.status}</span>
                {b.meeting_link && (
                  <a href={b.meeting_link} target="_blank" rel="noreferrer" className="text-[var(--if-gold)] underline break-all">{b.meeting_link}</a>
                )}
                {b.admin_notes && <span className="text-white/50">Note: {b.admin_notes}</span>}
              </div>
            )}
          </div>
        );
      })}
      {p.items.length === 0 && !p.loading && <p className="text-sm text-white/50 text-center py-8">No bookings yet.</p>}
      <PageControls {...p} label="bookings" />
    </div>
  );
}

function ContactsTab() {
  const p = usePagination("/admin/contacts", 25);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ status: "new", admin_notes: "" });

  const startEdit = (c) => {
    setEditing(c.id);
    setForm({ status: c.status || "new", admin_notes: c.admin_notes || "" });
  };
  const save = async () => {
    try {
      await api.patch(`/admin/contacts/${editing}`, form);
      setEditing(null);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed."); }
  };
  const remove = async (c) => {
    if (!window.confirm(`Delete contact submission from ${c.first_name} ${c.last_name}?`)) return;
    try {
      await api.delete(`/admin/contacts/${c.id}`);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="flex flex-col gap-3">
      {p.items.map((c) => {
        const isEdit = editing === c.id;
        const statusCls =
          c.status === "closed" ? "bg-green-500/15 text-green-400" :
          c.status === "in_progress" ? "bg-amber-500/15 text-amber-300" :
          "bg-[var(--if-gold)]/15 text-[var(--if-gold)]";
        return (
          <div key={c.id} className="card-dark p-5" data-testid={`admin-contact-${c.id}`}>
            <div className="flex justify-between items-start gap-4 mb-2">
              <div>
                <div className="font-display text-lg text-white">{c.first_name} {c.last_name}</div>
                <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
                  <a href={`mailto:${c.email}`} className="text-[var(--if-gold)] underline">{c.email}</a> · {c.phone || "no phone"} · {c.service || "no service"}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-xs text-white/40">{new Date(c.created_at).toLocaleDateString()}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-wider ${statusCls}`}>{(c.status || "new").replace(/_/g, " ")}</span>
              </div>
            </div>
            <p className="text-sm text-white/75 mt-2 mb-3" style={{ fontFamily: "Inter, sans-serif" }}>{c.message}</p>
            {c.admin_notes && !isEdit && (
              <p className="text-xs text-white/55 italic" style={{ fontFamily: "Inter, sans-serif" }}>📝 {c.admin_notes}</p>
            )}
            {isEdit ? (
              <div className="grid sm:grid-cols-2 gap-3 mt-3">
                <div>
                  <label className="font-mono-label text-white/50 block mb-1">Status</label>
                  <select className="if-input" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                    <option value="new">New</option>
                    <option value="in_progress">In Progress</option>
                    <option value="closed">Closed</option>
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <label className="font-mono-label text-white/50 block mb-1">Admin notes</label>
                  <textarea className="if-input" rows={2} value={form.admin_notes}
                    onChange={(e) => setForm({ ...form, admin_notes: e.target.value })} />
                </div>
                <div className="flex gap-2 sm:col-span-2">
                  <button onClick={save} className="btn-gold !py-2 !px-4 !text-[11px]"><Save className="w-3.5 h-3.5" /> Save</button>
                  <button onClick={() => setEditing(null)} className="btn-outline !py-2 !px-4 !text-[11px]"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ) : (
              <div className="flex gap-2 mt-3">
                <button onClick={() => startEdit(c)} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid={`edit-contact-${c.id}`}>
                  <Edit className="w-3.5 h-3.5" /> Edit
                </button>
                <button onClick={() => remove(c)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all" data-testid={`delete-contact-${c.id}`}>
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            )}
          </div>
        );
      })}
      {p.items.length === 0 && !p.loading && <p className="text-sm text-white/50 text-center py-8">No contact submissions yet.</p>}
      <PageControls {...p} label="contacts" />
    </div>
  );
}

function EbookBuyersCard() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState("");
  const reload = () => api.get("/admin/ebook/purchases").then(({ data }) => setItems(data.items)).catch(() => {});
  useEffect(() => { reload(); }, []);
  const reset = async (b) => {
    if (!window.confirm(`Reset ${b.email}'s ebook link? They get 2 fresh downloads + 7 more days, and a new email.`)) return;
    setBusy(b.id);
    try { await api.post(`/admin/ebook/purchases/${b.id}/reset`); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || "Reset failed."); }
    finally { setBusy(""); }
  };
  if (items.length === 0) return null;
  return (
    <div className="card-dark p-5 mb-4" data-testid="ebook-buyers-card">
      <div className="font-mono-label text-[var(--if-gold)] mb-3">Ebook Buyers — From Denied to Approved</div>
      <div className="flex flex-col gap-2">
        {items.map((b) => {
          const expired = new Date(b.expires_at) < new Date();
          return (
            <div key={b.id} className="flex flex-wrap items-center justify-between gap-2 border border-white/10 rounded-lg px-3 py-2">
              <div className="text-xs text-white/70" style={{ fontFamily: "Inter, sans-serif" }}>
                <span className="text-white">{b.email}</span> · {b.downloads_used}/{b.max_downloads} downloads ·{" "}
                <span className={expired ? "text-red-300" : ""}>{expired ? "expired" : `expires ${String(b.expires_at).slice(0, 10)}`}</span>
              </div>
              <button onClick={() => reset(b)} disabled={busy === b.id}
                className="px-3 py-1.5 rounded-full border border-[var(--if-gold)]/40 text-[var(--if-gold)] text-[11px] hover:bg-[var(--if-gold)]/10 transition-all disabled:opacity-50"
                data-testid={`ebook-reset-${b.id}`}>
                {busy === b.id ? "Resetting…" : "Reset link"}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PaymentsTab() {
  const p = usePagination("/admin/payments", 25);
  const [busy, setBusy] = useState("");
  const remove = async (pay) => {
    if (!window.confirm(`Delete payment record (${pay.session_id.slice(-12)})? Only stale pending/failed entries can be removed.`)) return;
    try {
      await api.delete(`/admin/payments/${encodeURIComponent(pay.session_id)}`);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };
  const resendReceipt = async (pay) => {
    setBusy(`receipt:${pay.session_id}`);
    try {
      const { data } = await api.post(`/admin/payments/${encodeURIComponent(pay.session_id)}/resend-receipt`);
      alert(`Receipt resent to ${data.sent_to}.`);
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't resend receipt."); }
    finally { setBusy(""); }
  };
  const refund = async (pay) => {
    const input = window.prompt(
      `Refund this payment?\n\nLeave blank for FULL refund of $${(pay.amount || 0).toFixed(2)}, ` +
      `or type a dollar amount for a partial refund.`,
      ""
    );
    if (input === null) return; // cancelled
    const trimmed = input.trim();
    const amount = trimmed === "" ? null : parseFloat(trimmed);
    if (trimmed !== "" && (!isFinite(amount) || amount <= 0)) {
      alert("Enter a positive dollar amount, or leave blank for a full refund.");
      return;
    }
    if (!window.confirm(amount ? `Confirm partial refund of $${amount.toFixed(2)}?` : "Confirm FULL refund?")) return;
    setBusy(`refund:${pay.session_id}`);
    try {
      const { data } = await api.post(`/admin/payments/${encodeURIComponent(pay.session_id)}/refund`,
        amount ? { amount, reason: "requested_by_customer" } : { reason: "requested_by_customer" });
      alert(`Refunded $${data.amount_refunded.toFixed(2)} — status: ${data.status}`);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Refund failed."); }
    finally { setBusy(""); }
  };

  return (
    <div className="flex flex-col gap-3">
      <EbookBuyersCard />
      {p.items.map((pay) => {
        const canDelete = pay.payment_status !== "paid";
        const isCash = pay.kind === "cash";
        const canAct = pay.payment_status === "paid" && !isCash;
        const lastRefund = pay.last_refund_amount;
        return (
          <div key={pay.session_id} className="card-dark p-5" data-testid={`admin-payment-${pay.session_id}`}>
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div className="min-w-0">
                <div className="font-display text-base text-white">{pay.package_id}</div>
                <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
                  ${pay.amount?.toFixed(2) || "0.00"} {pay.currency?.toUpperCase()} · {pay.metadata?.user_email || "anonymous"}
                </div>
                <div className="text-[10px] text-white/35 mt-1 break-all" style={{ fontFamily: "Inter, sans-serif" }}>{pay.session_id}</div>
                {lastRefund != null && (
                  <div className="text-[11px] text-amber-300 mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
                    Refunded ${lastRefund.toFixed(2)} · {pay.last_refund_status}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {isCash && <span className="px-2.5 py-1 rounded-full text-xs bg-emerald-500/15 text-emerald-300 font-semibold tracking-wide" data-testid={`payment-cash-badge-${pay.session_id}`}>CASH</span>}
                <span className={`px-2.5 py-1 rounded-full text-xs capitalize ${paymentStatusBadgeClass(pay.payment_status)}`}>{pay.payment_status}</span>
              </div>
            </div>
            {(canAct || canDelete) && (
              <div className="flex flex-wrap items-center gap-2 mt-3 pt-3 border-t border-white/8">
                {canAct && (
                  <>
                    <button
                      onClick={() => resendReceipt(pay)}
                      disabled={busy === `receipt:${pay.session_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[var(--if-gold)]/40 text-[var(--if-gold)] text-[11px] hover:bg-[var(--if-gold)]/10 transition-all disabled:opacity-40"
                      data-testid={`resend-receipt-${pay.session_id}`}
                    >
                      <MailCheck className="w-3.5 h-3.5" /> {busy === `receipt:${pay.session_id}` ? "Sending…" : "Resend receipt"}
                    </button>
                    <button
                      onClick={() => refund(pay)}
                      disabled={busy === `refund:${pay.session_id}`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-amber-400/40 text-amber-300 text-[11px] hover:bg-amber-400/10 transition-all disabled:opacity-40"
                      data-testid={`refund-payment-${pay.session_id}`}
                    >
                      <DollarSign className="w-3.5 h-3.5" /> {busy === `refund:${pay.session_id}` ? "Processing…" : "Refund"}
                    </button>
                  </>
                )}
                <button
                  onClick={() => remove(pay)}
                  disabled={!canDelete}
                  title={canDelete ? "Delete pending/failed record" : "Paid transactions can't be deleted (audit trail). Use Refund instead."}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid={`delete-payment-${pay.session_id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
              </div>
            )}
          </div>
        );
      })}
      {p.items.length === 0 && !p.loading && <p className="text-sm text-white/50 text-center py-8">No payments yet.</p>}
      <PageControls {...p} label="payments" />
    </div>
  );
}

function UsersTab() {
  const { user: me } = useAuth();
  const p = usePagination("/admin/users", 25);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: "", role: "client", phone: "" });
  const [sens, setSens] = useState({ ssn: "", card_number: "", card_cvv: "" });
  const [sensMeta, setSensMeta] = useState({});
  const [revealed, setRevealed] = useState(false);
  const [charging, setCharging] = useState(null); // user obj or null
  const [chargeForm, setChargeForm] = useState({ amount: "", description: "", send_email: true, method: "link", useNewCard: false, recurring: false });
  const [chargeBusy, setChargeBusy] = useState(false);
  const [chargeResult, setChargeResult] = useState(null);
  const [savedCard, setSavedCard] = useState(null); // {brand,last4,exp_month,exp_year} or null
  const [userRecurring, setUserRecurring] = useState([]); // active recurring charges for this user
  const cardFormRef = useRef(null);
  const [cashUser, setCashUser] = useState(null);
  const [cashForm, setCashForm] = useState({ amount: "", plan_name: "", note: "", set_subscription: true });
  const [cashBusy, setCashBusy] = useState(false);
  const [cashResult, setCashResult] = useState(null);

  const [mcUser, setMcUser] = useState(null);
  const [mcForm, setMcForm] = useState({ amount: "", note: "" });
  const [mcBusy, setMcBusy] = useState(false);
  const [mcResult, setMcResult] = useState(null);
  const [customSources, setCustomSources] = useState([]);
  useEffect(() => {
    api.get("/admin/referral-sources").then(({ data }) => setCustomSources(data.items)).catch(() => {});
  }, []);
  const openMarkCurrent = (u) => {
    setMcUser(u);
    setMcForm({ amount: u.subscription_amount ? String(u.subscription_amount) : "", note: "" });
    setMcResult(null);
  };
  const closeMarkCurrent = () => { setMcUser(null); setMcResult(null); };
  const submitMarkCurrent = async () => {
    const amount = parseFloat(mcForm.amount);
    if (!isFinite(amount) || amount <= 0) { alert("Enter the amount you collected (greater than $0)."); return; }
    setMcBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${mcUser.id}/mark-current`, {
        amount,
        note: mcForm.note.trim(),
      });
      setMcResult(data);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't mark the client current."); }
    finally { setMcBusy(false); }
  };

  const openCash = (u) => {
    const hasCashSub = (u.subscription_id || "").startsWith("cash_") && u.subscription_status === "active";
    setCashForm({
      amount: hasCashSub ? String(u.subscription_amount ?? "") : "",
      plan_name: hasCashSub ? (u.subscription_package_name || "") : "",
      note: "",
      set_subscription: true,
    });
    setCashResult(null);
    setCashUser(u);
  };
  const closeCash = () => { setCashUser(null); setCashResult(null); };

  const submitCash = async () => {
    const amount = parseFloat(cashForm.amount);
    if (!isFinite(amount) || amount <= 0) { alert("Enter the cash amount they paid."); return; }
    if (cashForm.set_subscription && !cashForm.plan_name.trim()) { alert("Give the plan a name (e.g. 'Credit Restoration — Cash')."); return; }
    setCashBusy(true);
    try {
      const { data } = await api.post(`/admin/users/${cashUser.id}/cash-payment`, {
        amount,
        plan_name: cashForm.plan_name.trim() || "Cash Payment",
        note: cashForm.note.trim(),
        set_subscription: cashForm.set_subscription,
      });
      setCashResult(data);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't record the cash payment."); }
    finally { setCashBusy(false); }
  };

  const cancelCashSub = async () => {
    if (!window.confirm(`Cancel ${cashUser.name || cashUser.email}'s cash subscription? Their payment history stays, but they'll no longer show as an active subscriber.`)) return;
    setCashBusy(true);
    try {
      await api.post(`/admin/users/${cashUser.id}/cash-subscription/cancel`);
      await p.reload();
      closeCash();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't cancel."); }
    finally { setCashBusy(false); }
  };

  const openCharge = async (u) => {
    setCharging(u);
    setChargeForm({ amount: "", description: "", send_email: true, method: "link", useNewCard: false, recurring: false });
    setChargeResult(null);
    setSavedCard(null);
    setUserRecurring([]);
    // Best-effort fetch the saved card so we can show "•••• 1234 on file"
    try {
      const { data } = await api.get(`/admin/users/${u.id}/payment-method`);
      setSavedCard(data?.card || null);
    } catch (e) { /* non-fatal */ }
    try {
      const { data } = await api.get(`/admin/users/${u.id}/recurring-charges`);
      setUserRecurring(data?.items || []);
    } catch (e) { /* non-fatal */ }
  };
  const closeCharge = () => { setCharging(null); setChargeResult(null); setSavedCard(null); setUserRecurring([]); };

  const cancelRecurring = async (rc) => {
    if (!window.confirm(`Stop the $${rc.amount}/mo auto-charge on ${charging.name || charging.email}'s card? They won't be charged again from next cycle.`)) return;
    try {
      await api.delete(`/admin/recurring-charges/${rc.id}`);
      setUserRecurring(userRecurring.filter(x => x.id !== rc.id));
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't cancel."); }
  };

  const submitCharge = async () => {
    const amount = parseFloat(chargeForm.amount);
    if (!isFinite(amount) || amount < 0.5) { alert("Enter a valid amount (minimum $0.50)."); return; }
    if (!chargeForm.description.trim()) { alert("Add a short description of what they're paying for."); return; }

    if (chargeForm.method === "link") {
      if (chargeForm.recurring) { alert("Recurring billing requires a card on file. Switch to 'Enter Card & Charge'."); return; }
      setChargeBusy(true);
      try {
        const { data } = await api.post(`/admin/users/${charging.id}/custom-charge`, {
          amount,
          description: chargeForm.description.trim(),
          send_email: chargeForm.send_email,
          method: "link",
        });
        setChargeResult(data);
      } catch (e) { alert(e?.response?.data?.detail || "Couldn't create the payment link."); }
      finally { setChargeBusy(false); }
      return;
    }

    // Card mode
    const useNew = chargeForm.useNewCard || !savedCard;

    if (chargeForm.recurring) {
      const ok = window.confirm(
        `Set up ${charging.name || charging.email} to be auto-charged $${amount.toFixed(2)} every month` +
        (useNew ? " on the new card you just entered" : ` on the saved card ending in ${savedCard.last4}`) +
        `?\n\nThe first charge will go through immediately. You can cancel anytime from their user row.`
      );
      if (!ok) return;
      setChargeBusy(true);
      try {
        let payload = { amount, description: chargeForm.description.trim(), interval: "month" };
        if (useNew) {
          const pmId = await cardFormRef.current.createPaymentMethod({
            name: charging.name || "", email: charging.email || "",
          });
          payload.payment_method_id = pmId;
        }
        const { data } = await api.post(`/admin/users/${charging.id}/recurring-charge`, payload);
        setChargeResult({ ...data, method: "recurring" });
      } catch (e) {
        alert(e?.response?.data?.detail || e?.message || "Couldn't set up recurring charge.");
      } finally { setChargeBusy(false); }
      return;
    }

    // One-off card charge
    const ok = window.confirm(`Charge ${charging.name || charging.email} $${amount.toFixed(2)}${useNew ? " on the new card you just entered" : ` on the saved card ending in ${savedCard.last4}`}? This will be processed immediately.`);
    if (!ok) return;

    setChargeBusy(true);
    try {
      let payload = {
        amount,
        description: chargeForm.description.trim(),
        send_email: false,
      };
      if (useNew) {
        const pmId = await cardFormRef.current.createPaymentMethod({
          name: charging.name || "",
          email: charging.email || "",
        });
        payload = { ...payload, method: "new_card", payment_method_id: pmId };
      } else {
        payload = { ...payload, method: "charge_now" };
      }
      const { data } = await api.post(`/admin/users/${charging.id}/custom-charge`, payload);
      if (data && data.requires_action && data.payment_intent_client_secret) {
        try {
          const pi = await cardFormRef.current.confirmCardAction(data.payment_intent_client_secret);
          setChargeResult({ ...data, ok: true, status: pi.status, requires_action: false });
        } catch (e) {
          alert("3D Secure verification failed: " + e.message);
        }
      } else {
        setChargeResult(data);
      }
    } catch (e) {
      alert(e?.response?.data?.detail || e?.message || "Couldn't process the payment.");
    } finally { setChargeBusy(false); }
  };
  const copyToClipboard = (text) => {
    navigator.clipboard?.writeText(text);
    alert("Payment link copied to clipboard.");
  };

  const startEdit = async (u) => {
    setEditing(u.id);
    setRevealed(false);
    setSens({ ssn: "", card_number: "", card_cvv: "" });
    setSensMeta({});
    setForm({
      name: u.name || "", role: u.role || "client", phone: u.phone || "", email: u.email || "",
      address_line1: u.address_line1 || "", address_line2: u.address_line2 || "", city: u.city || "",
      state: u.state || "", postal_code: u.postal_code || "", dob: u.dob || "", card_exp: "", card_zip: "",
    });
    try {
      const { data } = await api.get(`/admin/users/${u.id}/secure-profile`);
      setSensMeta({ ssn_masked: data.ssn_masked, card_masked: data.card_masked, has_cvv: data.has_cvv });
      setForm((f) => ({ ...f, dob: data.dob || "", card_exp: data.card_exp || "", card_zip: data.card_zip || "" }));
    } catch {}
  };
  const revealSensitive = async () => {
    try {
      const { data } = await api.get(`/admin/users/${editing}/sensitive/reveal`);
      setSens({ ssn: data.ssn || "", card_number: data.card_number || "", card_cvv: data.card_cvv || "" });
      setRevealed(true);
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't reveal the secure data."); }
  };
  const setReferralSource = async (u, value) => {
    if (value === "__add__") {
      const name = window.prompt("Name the new source (e.g. 'John Smith — Realtor'):");
      if (!name || name.trim().length < 2) return;
      try {
        const { data } = await api.post("/admin/referral-sources", { name: name.trim() });
        setCustomSources((prev) => (prev.includes(data.name) ? prev : [...prev, data.name].sort()));
        await api.patch(`/admin/users/${u.id}`, { referral_source: data.name });
        await p.reload();
      } catch (e) { alert(e?.response?.data?.detail || "Couldn't add the source."); }
      return;
    }
    try {
      await api.patch(`/admin/users/${u.id}`, { referral_source: value });
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't save referral source."); }
  };
  const save = async () => {
    try {
      const { card_exp, card_zip, dob, ...profile } = form;
      await api.patch(`/admin/users/${editing}`, profile);
      const sp = {};
      if (sens.ssn.trim()) sp.ssn = sens.ssn.trim();
      if (sens.card_number.trim()) sp.card_number = sens.card_number.trim();
      if (sens.card_cvv.trim()) sp.card_cvv = sens.card_cvv.trim();
      sp.dob = dob || "";
      sp.card_exp = card_exp || "";
      sp.card_zip = card_zip || "";
      await api.patch(`/admin/users/${editing}/sensitive`, sp);
      setEditing(null);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed."); }
  };
  const setLastWorkedDate = async (u, value) => {
    try {
      await api.patch(`/admin/users/${u.id}`, { last_worked_at: value });
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Couldn't save the date."); }
  };
  const remove = async (u) => {
    if (!window.confirm(`Delete user ${u.name || u.email} and ALL their data (bookings, documents, disputes, chat history)? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      await p.reload();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };

  return (
    <div className="flex flex-col gap-3">
      {p.items.map((u) => {
        const isEdit = editing === u.id;
        const isMe = u.id === me?.id;
        return (
          <div key={u.id} className="card-dark p-5" data-testid={`admin-user-${u.id}`}>
            <div className="flex flex-wrap justify-between items-center gap-4">
              <div className="flex items-center gap-3">
                {u.picture ? (
                  <img src={u.picture} alt="" className="w-10 h-10 rounded-full object-cover" />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-[var(--if-gold)]/20 flex items-center justify-center text-[var(--if-gold)] font-bold">
                    {u.name?.[0]?.toUpperCase() || u.email?.[0]?.toUpperCase() || "?"}
                  </div>
                )}
                <div>
                  <div className="font-display text-base text-white">{u.name || "—"} {isMe && <span className="text-[10px] text-[var(--if-gold)]">(you)</span>}</div>
                  <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}><a href={`mailto:${u.email}`} className="text-[var(--if-gold)] underline">{u.email}</a>{u.phone ? ` · ${u.phone}` : ""}</div>
                  <div className="text-[10px] text-white/35 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>Joined {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</div>
                  <div className="mt-1.5 flex flex-wrap items-center gap-2">
                    <select
                      className="text-[11px] bg-white border border-[#23261C]/20 rounded-md px-2 py-1 text-[#23261C]/75 outline-none focus:border-[#4F5D1E]/60 cursor-pointer"
                      style={{ fontFamily: "Inter, sans-serif" }}
                      value={u.referral_source || ""}
                      onChange={(e) => setReferralSource(u, e.target.value)}
                      title="Where did this client come from?"
                      data-testid={`referral-source-${u.id}`}
                    >
                      <option value="">Source: not set</option>
                      {[...REFERRAL_SOURCES, ...customSources.filter((c) => !REFERRAL_SOURCES.includes(c))]
                        .concat(u.referral_source && !REFERRAL_SOURCES.includes(u.referral_source) && !customSources.includes(u.referral_source) ? [u.referral_source] : [])
                        .map((s) => <option key={s} value={s}>{s}</option>)}
                      <option value="__add__">+ Add my own source…</option>
                    </select>
                    <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider text-white/40" style={{ fontFamily: "Inter, sans-serif" }}>
                      Last worked
                      <input
                        type="date"
                        value={u.last_worked_at || ""}
                        onChange={(e) => setLastWorkedDate(u, e.target.value)}
                        className="text-[11px] bg-white border border-[#23261C]/20 rounded-md px-2 py-1 text-[#23261C]/75 outline-none focus:border-[#4F5D1E]/60 cursor-pointer"
                        style={{ fontFamily: "Inter, sans-serif" }}
                        title="Last time you worked this client's file"
                        data-testid={`last-worked-${u.id}`}
                      />
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {u.subscription_status && ["active", "trialing", "past_due"].includes(u.subscription_status) ? (
                  <span
                    className={`px-2 py-1 rounded-full text-xs capitalize ${
                      u.subscription_status === "active" ? "bg-green-500/15 text-green-400" :
                      u.subscription_status === "trialing" ? "bg-blue-500/15 text-blue-300" :
                      "bg-amber-500/15 text-amber-300"
                    }`}
                    title={u.subscription_package_name || u.subscription_package_id || ""}
                    data-testid={`user-sub-status-${u.id}`}
                  >
                    {u.subscription_status === "active" ? "Subscriber" : u.subscription_status} · ${(u.subscription_amount || 0).toFixed(2)}/mo
                  </span>
                ) : (
                  <span className="px-2 py-1 rounded-full bg-white/5 text-white/50 text-xs" title="No active paid plan — free account only">
                    Free account
                  </span>
                )}
                {(u.subscription_id || "").startsWith("cash_") && ["active", "trialing", "past_due"].includes(u.subscription_status) && (
                  <span className="px-2 py-1 rounded-full bg-emerald-500/15 text-emerald-300 text-xs font-semibold tracking-wide" title={`Cash plan: ${u.subscription_package_name || ""}`} data-testid={`user-cash-badge-${u.id}`}>
                    CASH
                  </span>
                )}
                {u.auth_provider === "google" && <span className="px-2 py-1 rounded-full bg-blue-500/15 text-blue-400 text-xs">Google</span>}
                <span className={`px-2 py-1 rounded-full capitalize text-xs ${userRoleBadgeClass(u.role)}`}>{u.role}</span>
                {!isEdit && (
                  <>
                    {u.subscription_status === "past_due" && (
                      <button
                        onClick={() => openMarkCurrent(u)}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-amber-400/50 text-amber-300 text-[11px] hover:bg-amber-400/10 transition-all"
                        title="Collected the past-due payment? Mark this client current"
                        data-testid={`mark-current-${u.id}`}
                      >
                        <Check className="w-3.5 h-3.5" /> Mark Current
                      </button>
                    )}
                    <Link
                      to={`/admin/clients/${u.id}/insights`}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-white/15 text-white/80 text-[11px] hover:border-[var(--if-gold)]/40 hover:text-[var(--if-gold)] transition-all"
                      title="Open client Insights page"
                      data-testid={`insights-user-${u.id}`}
                    >
                      <Activity className="w-3.5 h-3.5" /> Insights
                    </Link>
                    <button
                      onClick={() => openCharge(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-[var(--if-gold)]/40 text-[var(--if-gold)] text-[11px] hover:bg-[var(--if-gold)]/10 transition-all"
                      title="Send a custom payment link"
                      data-testid={`charge-user-${u.id}`}
                    >
                      <DollarSign className="w-3.5 h-3.5" /> Charge
                    </button>
                    <button
                      onClick={() => openCash(u)}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-emerald-500/40 text-emerald-300 text-[11px] hover:bg-emerald-500/10 transition-all"
                      title="Record a cash payment / mark as cash subscriber"
                      data-testid={`cash-user-${u.id}`}
                    >
                      <Banknote className="w-3.5 h-3.5" /> Cash
                    </button>
                    <button onClick={() => startEdit(u)} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid={`edit-user-${u.id}`}>
                      <Edit className="w-3.5 h-3.5" /> Edit
                    </button>
                    <button
                      onClick={() => remove(u)}
                      disabled={isMe}
                      title={isMe ? "You can't delete yourself" : "Delete user and all data"}
                      className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
                      data-testid={`delete-user-${u.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" /> Delete
                    </button>
                  </>
                )}
              </div>
            </div>
            {isEdit && (
              <div className="mt-4 space-y-4" data-testid={`user-edit-form-${u.id}`}>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Full Name</label>
                    <input className="if-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-edit-name" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Email</label>
                    <input className="if-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-edit-email" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Phone</label>
                    <input className="if-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="user-edit-phone" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Date of Birth</label>
                    <input className="if-input" type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} data-testid="user-edit-dob" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Role</label>
                    <select className="if-input" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} disabled={isMe} data-testid="user-edit-role">
                      <option value="client">Client</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                </div>
                <div className="grid sm:grid-cols-3 gap-3">
                  <div className="sm:col-span-2">
                    <label className="font-mono-label text-white/50 block mb-1">Address</label>
                    <input className="if-input" placeholder="Street address" value={form.address_line1} onChange={(e) => setForm({ ...form, address_line1: e.target.value })} data-testid="user-edit-address1" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Apt / Suite</label>
                    <input className="if-input" value={form.address_line2} onChange={(e) => setForm({ ...form, address_line2: e.target.value })} data-testid="user-edit-address2" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">City</label>
                    <input className="if-input" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} data-testid="user-edit-city" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">State</label>
                    <input className="if-input" maxLength={2} placeholder="TX" value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} data-testid="user-edit-state" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Zip Code</label>
                    <input className="if-input" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} data-testid="user-edit-zip" />
                  </div>
                </div>
                <div className="rounded-xl border border-[var(--if-gold)]/30 bg-[var(--if-gold)]/5 p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Lock className="w-3.5 h-3.5 text-[var(--if-gold)]" />
                      <span className="font-mono-label text-[var(--if-gold)]">Secure — encrypted at rest</span>
                    </div>
                    {(sensMeta.ssn_masked || sensMeta.card_masked || sensMeta.has_cvv) && !revealed && (
                      <button onClick={revealSensitive} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-[var(--if-gold)]/40 text-[var(--if-gold)] text-[11px] hover:bg-[var(--if-gold)]/10 transition-all" data-testid="user-edit-reveal-btn">
                        <Eye className="w-3.5 h-3.5" /> Reveal
                      </button>
                    )}
                  </div>
                  <div className="grid sm:grid-cols-3 gap-3">
                    <div>
                      <label className="font-mono-label text-white/50 block mb-1">Social (SSN)</label>
                      <input className="if-input" placeholder={sensMeta.ssn_masked || "123-45-6789"} value={sens.ssn} onChange={(e) => setSens({ ...sens, ssn: e.target.value })} data-testid="user-edit-ssn" />
                    </div>
                    <div className="sm:col-span-2">
                      <label className="font-mono-label text-white/50 block mb-1">Credit Card #</label>
                      <input className="if-input" placeholder={sensMeta.card_masked || "Card number"} value={sens.card_number} onChange={(e) => setSens({ ...sens, card_number: e.target.value })} data-testid="user-edit-card-number" />
                    </div>
                    <div>
                      <label className="font-mono-label text-white/50 block mb-1">Expiration</label>
                      <input className="if-input" placeholder="MM/YY" value={form.card_exp} onChange={(e) => setForm({ ...form, card_exp: e.target.value })} data-testid="user-edit-card-exp" />
                    </div>
                    <div>
                      <label className="font-mono-label text-white/50 block mb-1">CVV</label>
                      <input className="if-input" placeholder={sensMeta.has_cvv ? "••• (saved)" : "CVV"} maxLength={4} value={sens.card_cvv} onChange={(e) => setSens({ ...sens, card_cvv: e.target.value })} data-testid="user-edit-card-cvv" />
                    </div>
                    <div>
                      <label className="font-mono-label text-white/50 block mb-1">Card Zip Code</label>
                      <input className="if-input" value={form.card_zip} onChange={(e) => setForm({ ...form, card_zip: e.target.value })} data-testid="user-edit-card-zip" />
                    </div>
                  </div>
                  <p className="text-[10px] text-white/40 mt-2" style={{ fontFamily: "Inter, sans-serif" }}>
                    Saved values show masked. Type over a field to replace it, or hit Reveal to see the full numbers.
                  </p>
                </div>
                <div className="flex gap-2">
                  <button onClick={save} className="btn-gold !py-2 !px-4 !text-[11px]" data-testid="user-edit-save"><Save className="w-3.5 h-3.5" /> Save</button>
                  <button onClick={() => setEditing(null)} className="btn-outline !py-2 !px-4 !text-[11px]" data-testid="user-edit-cancel"><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            )}
          </div>
        );
      })}
      {p.items.length === 0 && !p.loading && <p className="text-sm text-white/50 text-center py-8">No users yet.</p>}
      <PageControls {...p} label="users" />

      {cashUser && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" data-testid="cash-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeCash} />
          <div className="relative w-full max-w-md card-dark border-emerald-500/40 p-7">
            <button onClick={closeCash} className="absolute top-3 right-3 text-white/40 hover:text-white p-2" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
            {cashResult ? (
              <div className="text-center py-4" data-testid="cash-success">
                <div className="w-12 h-12 rounded-full bg-emerald-500/15 border border-emerald-500/40 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-emerald-300" />
                </div>
                <h3 className="font-display text-2xl text-white mb-2">Cash payment recorded.</h3>
                <p className="text-sm text-white/65" style={{ fontFamily: "Inter, sans-serif" }}>
                  ${cashResult.amount.toFixed(2)} logged for {cashUser.name || cashUser.email}.
                  {cashResult.subscription && (
                    <> They're now an <strong className="text-emerald-300">active cash subscriber</strong> on “{cashResult.subscription.plan_name}”.</>
                  )}
                </p>
                <button onClick={closeCash} className="btn-gold !py-2 !px-5 !text-[11px] mt-5" data-testid="cash-done">Done</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Banknote className="w-4 h-4 text-emerald-300" />
                  <span className="font-mono-label text-emerald-300">Cash Payment</span>
                </div>
                <h3 className="font-display text-2xl text-white mb-1">{cashUser.name || cashUser.email}</h3>
                <p className="text-xs text-white/55 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
                  Log a payment made in cash. It counts in your revenue and payment history.
                </p>
                {(cashUser.subscription_id || "").startsWith("cash_") && cashUser.subscription_status === "active" && (
                  <div className="rounded-lg border border-emerald-500/25 bg-emerald-500/8 px-3 py-2.5 mb-4 text-xs text-white/75" style={{ fontFamily: "Inter, sans-serif" }} data-testid="cash-active-info">
                    Active cash plan: <strong>{cashUser.subscription_package_name}</strong> — ${(cashUser.subscription_amount || 0).toFixed(2)}/mo.
                    Recording another payment logs their monthly renewal.
                  </div>
                )}
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Amount paid (USD)</label>
                    <input className="if-input" type="number" min="0" step="0.01" placeholder="e.g. 69.99" value={cashForm.amount}
                      onChange={(e) => setCashForm({ ...cashForm, amount: e.target.value })} data-testid="cash-amount" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Plan name</label>
                    <input className="if-input" placeholder="e.g. Credit Restoration — Cash" value={cashForm.plan_name}
                      onChange={(e) => setCashForm({ ...cashForm, plan_name: e.target.value })} data-testid="cash-plan-name" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Note (optional)</label>
                    <input className="if-input" placeholder="e.g. First month paid in office" value={cashForm.note}
                      onChange={(e) => setCashForm({ ...cashForm, note: e.target.value })} data-testid="cash-note" />
                  </div>
                  <label className="flex items-start gap-2 text-xs text-white/70 cursor-pointer" style={{ fontFamily: "Inter, sans-serif" }}>
                    <input type="checkbox" className="mt-0.5" checked={cashForm.set_subscription}
                      onChange={(e) => setCashForm({ ...cashForm, set_subscription: e.target.checked })} data-testid="cash-set-subscription" />
                    <span>Mark as <strong>active subscriber</strong> (counts in MRR with a CASH badge). Stays active until you cancel it.</span>
                  </label>
                  <div className="flex items-center justify-between gap-2 mt-2">
                    {(cashUser.subscription_id || "").startsWith("cash_") && cashUser.subscription_status === "active" ? (
                      <button onClick={cancelCashSub} disabled={cashBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all disabled:opacity-50"
                        data-testid="cash-cancel-sub">
                        Cancel cash plan
                      </button>
                    ) : <span />}
                    <div className="flex gap-2">
                      <button onClick={closeCash} className="btn-outline !py-2 !px-4 !text-[11px]">Close</button>
                      <button onClick={submitCash} disabled={cashBusy} className="btn-gold !py-2 !px-4 !text-[11px] disabled:opacity-60" data-testid="cash-submit">
                        {cashBusy ? "Recording…" : "Record Payment"}
                      </button>
                    </div>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {mcUser && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" data-testid="mark-current-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeMarkCurrent} />
          <div className="relative w-full max-w-md card-dark border-amber-400/40 p-7">
            <button onClick={closeMarkCurrent} className="absolute top-3 right-3 text-white/40 hover:text-white p-2" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
            {mcResult ? (
              <div className="text-center py-4" data-testid="mark-current-success">
                <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mx-auto mb-4">
                  <Check className="w-6 h-6 text-green-400" />
                </div>
                <h3 className="font-display text-2xl text-white mb-2">Client is current.</h3>
                <p className="text-sm text-white/65" style={{ fontFamily: "Inter, sans-serif" }}>
                  ${mcResult.amount.toFixed(2)} logged for {mcUser.name || mcUser.email}.
                  {mcResult.invoices_cleared > 0 && (
                    <> {mcResult.invoices_cleared} open Stripe invoice{mcResult.invoices_cleared > 1 ? "s" : ""} marked paid — Stripe will stop chasing it and billing continues normally.</>
                  )}
                </p>
                {mcResult.stripe_warning && (
                  <p className="text-xs text-amber-300 mt-3" style={{ fontFamily: "Inter, sans-serif" }} data-testid="mark-current-warning">
                    {mcResult.stripe_warning}
                  </p>
                )}
                <button onClick={closeMarkCurrent} className="btn-gold !py-2 !px-5 !text-[11px] mt-5" data-testid="mark-current-done">Done</button>
              </div>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-1">
                  <Check className="w-4 h-4 text-amber-300" />
                  <span className="font-mono-label text-amber-300">Mark Current</span>
                </div>
                <h3 className="font-display text-2xl text-white mb-1">{mcUser.name || mcUser.email}</h3>
                <p className="text-xs text-white/55 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
                  Collected the past-due payment outside of Stripe? This logs the payment,
                  {!(mcUser.subscription_id || "").startsWith("cash_") && " marks the open Stripe invoice as paid (out of band) so billing continues normally,"} and sets the client back to <strong className="text-green-400">Current</strong>.
                </p>
                <div className="flex flex-col gap-3">
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Amount collected (USD)</label>
                    <input className="if-input" type="number" min="0" step="0.01" placeholder="e.g. 99.00" value={mcForm.amount}
                      onChange={(e) => setMcForm({ ...mcForm, amount: e.target.value })} data-testid="mark-current-amount" />
                  </div>
                  <div>
                    <label className="font-mono-label text-white/50 block mb-1">Note (optional)</label>
                    <input className="if-input" placeholder="e.g. Paid in cash at office 6/12" value={mcForm.note}
                      onChange={(e) => setMcForm({ ...mcForm, note: e.target.value })} data-testid="mark-current-note" />
                  </div>
                  <div className="flex justify-end gap-2 mt-2">
                    <button onClick={closeMarkCurrent} className="btn-outline !py-2 !px-4 !text-[11px]">Close</button>
                    <button onClick={submitMarkCurrent} disabled={mcBusy} className="btn-gold !py-2 !px-4 !text-[11px] disabled:opacity-60" data-testid="mark-current-submit">
                      {mcBusy ? "Updating…" : "Log Payment & Mark Current"}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {charging && (
        <div className="fixed inset-0 z-[120] flex items-center justify-center px-4" data-testid="charge-modal">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={closeCharge} />
          <div className="relative w-full max-w-md card-dark border-[var(--if-gold)]/40 p-7">
            <button onClick={closeCharge} className="absolute top-3 right-3 text-white/40 hover:text-white p-2" aria-label="Close">
              <X className="w-4 h-4" />
            </button>
            {!chargeResult ? (
              <>
                <div className="font-mono-label text-[var(--if-gold)] mb-1">Custom Payment</div>
                <h2 className="font-display text-2xl mb-1">Charge {charging.name || charging.email}</h2>
                <p className="text-sm text-white/55 mb-5" style={{ fontFamily: "Inter, sans-serif" }}>
                  {chargeForm.method === "card"
                    ? "Charges the client's card immediately. Card stays on file for future one-click charges. The transaction appears as paid on their dashboard."
                    : "Generates a one-time secure Stripe link. We can email it to the client or you can share it directly. Appears as 'Pending' on their dashboard until paid."}
                </p>

                {userRecurring.length > 0 && (
                  <div className="mb-4 p-3 rounded-lg border border-green-500/25 bg-green-500/5" data-testid="active-recurring-list">
                    <div className="font-mono-label text-[10px] text-green-300 mb-1.5">ACTIVE AUTO-CHARGES</div>
                    {userRecurring.map((rc) => (
                      <div key={rc.id} className="flex items-center justify-between gap-2 py-1 text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
                        <div className="min-w-0">
                          <div className="text-white truncate">{rc.description}</div>
                          <div className="text-[10px] text-white/45">
                            ${rc.amount.toFixed(2)}/mo · •••• {rc.card_last4 || "----"}{rc.current_period_end ? ` · next on ${new Date(rc.current_period_end).toLocaleDateString()}` : ""}
                          </div>
                        </div>
                        <button onClick={() => cancelRecurring(rc)} className="text-[11px] text-red-300 hover:text-red-200 hover:underline whitespace-nowrap" data-testid={`cancel-recurring-${rc.id}`}>
                          Cancel
                        </button>
                      </div>
                    ))}
                  </div>
                )}

                <div className="grid grid-cols-2 gap-2 mb-5">
                  <button
                    type="button"
                    onClick={() => setChargeForm({ ...chargeForm, method: "link" })}
                    className={`text-left px-3 py-3 rounded-lg border transition ${chargeForm.method === "link" ? "border-[var(--if-gold)] bg-[var(--if-gold)]/10" : "border-white/15 hover:border-white/30"}`}
                    data-testid="charge-method-link"
                  >
                    <div className="font-mono-label text-[10px] text-[var(--if-gold)] mb-1">OPTION A</div>
                    <div className="font-display text-base">Send Payment Link</div>
                    <div className="text-[11px] text-white/55 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>Client pays online</div>
                  </button>
                  <button
                    type="button"
                    onClick={() => setChargeForm({ ...chargeForm, method: "card" })}
                    className={`text-left px-3 py-3 rounded-lg border transition ${chargeForm.method === "card" ? "border-[var(--if-gold)] bg-[var(--if-gold)]/10" : "border-white/15 hover:border-white/30"}`}
                    data-testid="charge-method-card"
                  >
                    <div className="font-mono-label text-[10px] text-[var(--if-gold)] mb-1">OPTION B</div>
                    <div className="font-display text-base">Enter Card &amp; Charge</div>
                    <div className="text-[11px] text-white/55 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>You key in their card</div>
                  </button>
                </div>

                <div className="flex flex-col gap-3">
                  <label className="flex flex-col gap-1.5">
                    <span className="font-mono-label text-white/50">Amount (USD)</span>
                    <input
                      type="number" step="0.01" min="0.50" placeholder="e.g. 99.99"
                      value={chargeForm.amount}
                      onChange={(e) => setChargeForm({ ...chargeForm, amount: e.target.value })}
                      className="if-input"
                      data-testid="charge-amount"
                    />
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="font-mono-label text-white/50">Description (shown on receipt)</span>
                    <input
                      type="text" placeholder="e.g. Couples Credit Restoration Package"
                      value={chargeForm.description}
                      onChange={(e) => setChargeForm({ ...chargeForm, description: e.target.value })}
                      className="if-input"
                      maxLength={240}
                      data-testid="charge-description"
                    />
                  </label>
                  {chargeForm.method === "link" && (
                    <label className="flex items-center gap-2 text-sm text-white/75 cursor-pointer mt-1" style={{ fontFamily: "Inter, sans-serif" }}>
                      <input type="checkbox" checked={chargeForm.send_email}
                        onChange={(e) => setChargeForm({ ...chargeForm, send_email: e.target.checked })}
                        className="accent-[var(--if-gold)]" data-testid="charge-send-email" />
                      Email the payment link to <span className="text-[var(--if-gold)]">{charging.email}</span>
                    </label>
                  )}
                  {chargeForm.method === "card" && (
                    <div className="flex flex-col gap-2.5 mt-1" data-testid="charge-card-section">
                      <label className="flex items-start gap-2 cursor-pointer p-2.5 rounded-lg border border-white/10 hover:border-[var(--if-gold)]/40 transition" data-testid="charge-recurring-toggle-row">
                        <input
                          type="checkbox"
                          checked={chargeForm.recurring}
                          onChange={(e) => setChargeForm({ ...chargeForm, recurring: e.target.checked })}
                          className="accent-[var(--if-gold)] mt-0.5"
                          data-testid="charge-recurring-toggle"
                        />
                        <div className="flex-1">
                          <div className="text-sm text-white" style={{ fontFamily: "Inter, sans-serif" }}>Auto-charge this every month</div>
                          <div className="text-[11px] text-white/50 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                            {chargeForm.recurring
                              ? "First charge runs today. Future charges happen automatically on the same day each month. Counts toward MRR."
                              : "Charges once now. The card stays on file but won't be auto-charged again."}
                          </div>
                        </div>
                      </label>

                      {savedCard && !chargeForm.useNewCard ? (
                        <div className="flex items-start justify-between gap-3 p-3 rounded-lg border border-[var(--if-gold)]/30 bg-[var(--if-gold)]/5">
                          <div>
                            <div className="font-mono-label text-[10px] text-[var(--if-gold)]">CARD ON FILE</div>
                            <div className="font-display text-base mt-0.5" data-testid="saved-card-display">
                              {(savedCard.brand || "Card").toString().replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())} •••• {savedCard.last4}
                            </div>
                            <div className="text-[11px] text-white/50" style={{ fontFamily: "Inter, sans-serif" }}>
                              Exp {String(savedCard.exp_month).padStart(2, "0")}/{String(savedCard.exp_year).slice(-2)}
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={() => setChargeForm({ ...chargeForm, useNewCard: true })}
                            className="text-[11px] text-[var(--if-gold)] hover:underline whitespace-nowrap"
                            data-testid="use-new-card-btn"
                          >
                            Use a different card →
                          </button>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-center justify-between">
                            <span className="font-mono-label text-white/50">Card Details</span>
                            {savedCard && (
                              <button
                                type="button"
                                onClick={() => setChargeForm({ ...chargeForm, useNewCard: false })}
                                className="text-[11px] text-white/55 hover:text-white"
                              >
                                ← Use card on file (•••• {savedCard.last4})
                              </button>
                            )}
                          </div>
                          <StripeCardForm ref={cardFormRef} />
                          <p className="text-[11px] text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
                            This card will be saved as the default on file — future charges can be processed in one click.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
                <div className="flex justify-end gap-2 mt-5">
                  <button onClick={closeCharge} className="btn-outline !py-2 !px-4 !text-[11px]">Cancel</button>
                  <button onClick={submitCharge} disabled={chargeBusy} className="btn-gold !py-2 !px-5 !text-[12px]" data-testid="charge-submit">
                    {chargeBusy ? (chargeForm.method === "card" ? (chargeForm.recurring ? "Setting up…" : "Charging…") : "Creating…") : (
                      <><DollarSign className="w-4 h-4" />
                        {chargeForm.method === "card"
                          ? (chargeForm.recurring
                              ? `Start $${parseFloat(chargeForm.amount || 0).toFixed(2)}/mo auto-charge`
                              : (savedCard && !chargeForm.useNewCard ? "Charge card on file" : "Charge card"))
                          : "Create payment link"}
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : chargeResult.method === "recurring" ? (
              <>
                <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mb-4">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-display text-2xl mb-2" data-testid="recurring-success">Monthly auto-charge set up.</h2>
                <p className="text-sm text-white/65 mb-2" style={{ fontFamily: "Inter, sans-serif" }}>
                  <span className="text-[var(--if-gold)]">${chargeResult.amount.toFixed(2)}/mo</span>
                  {chargeResult.last4 ? <> · card ending in {chargeResult.last4}</> : null}
                </p>
                <p className="text-xs text-white/55 mb-3" style={{ fontFamily: "Inter, sans-serif" }}>
                  ✓ First charge captured today.
                  {chargeResult.current_period_end && <> Next charge: {new Date(chargeResult.current_period_end).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}.</>}
                </p>
                <p className="text-xs text-[var(--if-gold)] mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
                  This subscription counts toward MRR. You can cancel anytime from {charging.name || charging.email}&apos;s row.
                </p>
                <button onClick={closeCharge} className="btn-gold !py-2 !px-5 !text-[12px]">Done</button>
              </>
            ) : (chargeResult.method === "charge_now" || chargeResult.method === "new_card") ? (
              <>
                <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mb-4">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-display text-2xl mb-2" data-testid="charge-now-success">
                  {chargeResult.status === "succeeded" ? "Payment captured." : "Payment processing."}
                </h2>
                <p className="text-sm text-white/65 mb-2" style={{ fontFamily: "Inter, sans-serif" }}>
                  <span className="text-[var(--if-gold)]">${chargeResult.amount.toFixed(2)}</span>
                  {chargeResult.last4 ? <> · charged to card ending in {chargeResult.last4}</> : null}
                </p>
                {chargeResult.method === "new_card" && chargeResult.saved_as_default && (
                  <p className="text-xs text-green-300/80 mb-3" style={{ fontFamily: "Inter, sans-serif" }}>
                    ✓ Card saved as default — future charges can be one-click.
                  </p>
                )}
                <p className="text-xs text-white/45 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
                  Status: {chargeResult.status}. The client now sees this on their dashboard.
                </p>
                {chargeResult.receipt_url && (
                  <a href={chargeResult.receipt_url} target="_blank" rel="noopener noreferrer" className="btn-outline !py-2 !px-4 !text-[11px] inline-flex">
                    View Stripe receipt
                  </a>
                )}
                <button onClick={closeCharge} className="block text-xs text-white/45 hover:text-white/75 mt-4 underline-offset-2 hover:underline">
                  Done
                </button>
              </>
            ) : (
              <>
                <div className="w-12 h-12 rounded-full bg-green-500/15 border border-green-500/40 flex items-center justify-center mb-4">
                  <Check className="w-5 h-5 text-green-400" />
                </div>
                <h2 className="font-display text-2xl mb-2">Payment link created.</h2>
                <p className="text-sm text-white/65 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>
                  ${chargeResult.amount.toFixed(2)} · {chargeResult.emailed_to ? `Emailed to ${chargeResult.emailed_to}` : "Email skipped — copy the link below"}
                </p>
                <div className="bg-black/30 border border-white/15 rounded-lg p-3 text-[11px] text-white/70 break-all mb-4 font-mono">
                  {chargeResult.checkout_url}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => copyToClipboard(chargeResult.checkout_url)} className="btn-outline !py-2 !px-4 !text-[11px] flex-1">
                    Copy link
                  </button>
                  <a href={chargeResult.checkout_url} target="_blank" rel="noopener noreferrer" className="btn-gold !py-2 !px-4 !text-[11px] flex-1">
                    Open in Stripe
                  </a>
                </div>
                <button onClick={closeCharge} className="text-xs text-white/45 hover:text-white/75 mt-4 underline-offset-2 hover:underline">
                  Done
                </button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function fmtBytes(n) {
  if (!n) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function DocumentsTab() {
  const p = usePagination("/admin/documents", 25);
  const [downloadingId, setDownloadingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulk, setBulk] = useState(null); // {running, done, failed}
  const [bulkMsg, setBulkMsg] = useState("");
  const bulkTimer = useRef(null);

  const pollBulk = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/documents/bulk-status");
      setBulk(data);
      if (data.running > 0) {
        bulkTimer.current = setTimeout(pollBulk, 5000);
      } else {
        setBulkMsg((m) => (m ? "Bulk re-analysis complete." : m));
      }
    } catch { /* non-fatal */ }
  }, []);

  useEffect(() => {
    // If a bulk run is already in flight (e.g., page refresh), resume the progress banner
    (async () => {
      try {
        const { data } = await api.get("/admin/documents/bulk-status");
        if (data.running > 0) {
          setBulk(data);
          setBulkMsg("Re-analysis in progress…");
          bulkTimer.current = setTimeout(pollBulk, 5000);
        }
      } catch { /* non-fatal */ }
    })();
    return () => { if (bulkTimer.current) clearTimeout(bulkTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const bulkRegenerate = async () => {
    const ok = window.confirm(
      "Re-analyze ALL credit reports for ALL clients?\n\n" +
      "Every document that already has an analysis will be re-run with the latest AI engine " +
      "(2 at a time). This may take several minutes and uses AI credits per report."
    );
    if (!ok) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/documents/bulk-regenerate");
      setBulkMsg(`${data.message}${data.skipped_running ? ` Skipped ${data.skipped_running} already running.` : ""}`);
      if (bulkTimer.current) clearTimeout(bulkTimer.current);
      await pollBulk();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't start bulk re-analysis.");
    } finally {
      setBulkBusy(false);
    }
  };

  const open = async (d) => {
    setDownloadingId(d.id);
    try {
      const { data } = await api.get(`/documents/${d.id}/download`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } finally {
      setDownloadingId(null);
    }
  };

  const remove = async (d) => {
    if (!window.confirm(`Delete "${d.filename}"? This permanently removes it from Cloudinary.`)) return;
    setDeletingId(d.id);
    try {
      await api.delete(`/admin/documents/${d.id}`);
      await p.reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="card-dark p-4 flex flex-wrap items-center justify-between gap-3" data-testid="admin-bulk-regenerate-bar">
        <div className="min-w-0">
          <div className="font-display text-base text-white">Bulk AI Re-Analysis</div>
          <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
            Re-runs Sergeant Credit on every previously analyzed credit report, for all clients.
          </div>
          {(bulkMsg || (bulk && bulk.running > 0)) && (
            <div className="text-xs mt-1.5 text-[var(--if-gold)]" style={{ fontFamily: "Inter, sans-serif" }} data-testid="admin-bulk-status">
              {bulk && bulk.running > 0 ? (
                <span className="inline-flex items-center gap-1.5">
                  <Sparkles className="w-3.5 h-3.5 animate-pulse" />
                  {bulk.running} running · {bulk.done} done{bulk.failed ? ` · ${bulk.failed} failed` : ""}
                </span>
              ) : bulkMsg}
            </div>
          )}
        </div>
        <button
          onClick={bulkRegenerate}
          disabled={bulkBusy || (bulk && bulk.running > 0)}
          className="btn-gold !py-2 !px-4 !text-[11px] disabled:opacity-60 shrink-0"
          data-testid="admin-bulk-regenerate-btn"
        >
          <Sparkles className="w-3.5 h-3.5" />
          {bulkBusy ? "Starting…" : (bulk && bulk.running > 0) ? "Re-analyzing…" : "Re-Analyze All Reports"}
        </button>
      </div>
      {p.items.map((d) => (
        <div key={d.id} className="card-dark p-4 flex flex-wrap items-center justify-between gap-4" data-testid={`admin-document-${d.id}`}>
          <div className="flex items-center gap-3 min-w-0">
            <FileText className="w-5 h-5 text-[var(--if-gold)] shrink-0" />
            <div className="min-w-0">
              <div className="font-display text-base text-white truncate" title={d.filename}>{d.filename}</div>
              <div className="text-xs text-white/55 truncate" style={{ fontFamily: "Inter, sans-serif" }}>
                {d.user_name || "—"} · {d.user_email}
              </div>
              <div className="text-xs text-white/45 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                {fmtBytes(d.size_bytes)} · {new Date(d.created_at).toLocaleString()}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => open(d)}
              disabled={downloadingId === d.id}
              className="btn-outline !py-2 !px-3 !text-[11px]"
              data-testid={`admin-document-open-${d.id}`}
            >
              <Download className="w-3.5 h-3.5" />
              {downloadingId === d.id ? "…" : "Open"}
            </button>
            <button
              onClick={() => remove(d)}
              disabled={deletingId === d.id}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all disabled:opacity-50"
              data-testid={`admin-document-delete-${d.id}`}
            >
              <Trash2 className="w-3.5 h-3.5" />
              {deletingId === d.id ? "…" : "Delete"}
            </button>
          </div>
        </div>
      ))}
      {p.items.length === 0 && !p.loading && <p className="text-sm text-white/50 text-center py-8">No documents uploaded yet.</p>}
      <PageControls {...p} label="documents" />
    </div>
  );
}

function ContractsTab() {
  const inputCls = "w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors";
  const [templates, setTemplates] = useState([]);
  const [requests, setRequests] = useState([]);
  const [users, setUsers] = useState([]);
  const [uploadName, setUploadName] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [sendForm, setSendForm] = useState({ template_id: "", user_id: "", note: "" });
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const [t, r] = await Promise.all([api.get("/admin/contracts/templates"), api.get("/admin/contracts/requests")]);
    setTemplates(t.data.items || []);
    setRequests(r.data.items || []);
  };
  useEffect(() => {
    reload();
    api.get("/admin/users?limit=200").then(({ data }) => setUsers((data.items || []).filter((u) => u.role !== "admin"))).catch(() => {});
  }, []);

  const upload = async () => {
    if (!uploadFile) { alert("Choose a PDF file."); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("name", uploadName.trim() || uploadFile.name.replace(/\.pdf$/i, ""));
      fd.append("file", uploadFile);
      await api.post("/admin/contracts/templates", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploadName(""); setUploadFile(null);
      document.getElementById("contract-file-input").value = "";
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Upload failed."); }
    finally { setBusy(false); }
  };

  const send = async () => {
    if (!sendForm.template_id || !sendForm.user_id) { alert("Pick a contract and a client."); return; }
    setBusy(true);
    try {
      await api.post("/admin/contracts/send", sendForm);
      setSendForm({ template_id: "", user_id: "", note: "" });
      await reload();
      alert("Contract sent — the client has been emailed a signing link.");
    } catch (e) { alert(e?.response?.data?.detail || "Send failed."); }
    finally { setBusy(false); }
  };

  const voidReq = async (r) => {
    if (!window.confirm(`Void the pending "${r.template_name}" request for ${r.user_name || r.user_email}?`)) return;
    try { await api.post(`/admin/contracts/requests/${r.id}/void`); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || "Void failed."); }
  };

  const delTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"?`)) return;
    try { await api.delete(`/admin/contracts/templates/${t.id}`); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };

  const toggleAuto = async (t) => {
    const turningOn = !t.auto_send;
    if (turningOn && !window.confirm(`Auto-send "${t.name}" to every client the moment they subscribe (Stripe) or are marked as a cash subscriber?\n\nOnly one template can be on auto-send — it replaces any other.`)) return;
    try { await api.patch(`/admin/contracts/templates/${t.id}/auto-send`, { auto_send: turningOn }); await reload(); }
    catch (e) { alert(e?.response?.data?.detail || "Couldn't update auto-send."); }
  };

  const downloadSigned = async (r) => {
    try {
      const { data } = await api.get(`/documents/${r.signed_doc_id}/download`);
      window.open(data.url, "_blank", "noopener");
    } catch (e) { alert(e?.response?.data?.detail || "Download failed."); }
  };

  const statusCls = (s) => s === "signed" ? "bg-[#4F5D1E]/15 text-[#4F5D1E]" : s === "pending" ? "bg-amber-400/15 text-amber-300" : "bg-white/8 text-white/50";

  return (
    <div className="flex flex-col gap-4">
      <div className="grid lg:grid-cols-2 gap-4">
        <div className="card-dark p-5" data-testid="contracts-upload-card">
          <div className="font-display text-base text-white mb-1">Contract Templates</div>
          <p className="text-xs text-white/50 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>Upload your service agreement or any PDF contract once — send it to any client.</p>
          <div className="flex flex-col gap-2.5">
            <input className={inputCls} placeholder="Template name (e.g. Service Agreement)" value={uploadName}
              onChange={(e) => setUploadName(e.target.value)} data-testid="contract-template-name" />
            <input id="contract-file-input" type="file" accept="application/pdf" className="text-xs text-white/60 file:mr-3 file:btn-gold file:!py-1.5 file:!px-3 file:!text-[10px] file:border-0 file:rounded-full file:bg-[#546643] file:text-[#F7F5EF]"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)} data-testid="contract-template-file" />
            <button onClick={upload} disabled={busy} className="btn-gold !py-2 !px-4 !text-[11px] self-start disabled:opacity-60" data-testid="contract-template-upload">
              {busy ? "Uploading…" : "Upload Template"}
            </button>
          </div>
          <div className="flex flex-col gap-2 mt-4">
            {templates.map((t) => (
              <div key={t.id} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-2" data-testid={`contract-template-${t.id}`}>
                <div className="min-w-0">
                  <div className="text-sm text-white truncate flex items-center gap-2" style={{ fontFamily: "Inter, sans-serif" }}>
                    <span className="truncate">{t.name}</span>
                    {t.auto_send && (
                      <span className="px-2 py-0.5 rounded-full bg-[#4F5D1E]/15 text-[#4F5D1E] text-[9px] uppercase tracking-wider font-semibold shrink-0" data-testid={`contract-auto-badge-${t.id}`}>
                        Auto-Send
                      </span>
                    )}
                  </div>
                  <div className="text-[11px] text-white/40">{(t.size_bytes / 1024 / 1024).toFixed(2)} MB · {String(t.created_at).slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-1.5 shrink-0">
                  <button
                    onClick={() => toggleAuto(t)}
                    className={`px-2.5 py-1.5 rounded-full border text-[10px] transition-all ${t.auto_send ? "border-[#4F5D1E]/50 text-[#4F5D1E] hover:bg-[#4F5D1E]/10" : "border-white/15 text-white/50 hover:border-[#4F5D1E]/40 hover:text-[#4F5D1E]"}`}
                    title={t.auto_send ? "Turn off auto-send on new subscriptions" : "Auto-send to every new subscriber (Stripe or cash)"}
                    data-testid={`contract-auto-toggle-${t.id}`}
                  >
                    {t.auto_send ? "Auto: ON" : "Auto: OFF"}
                  </button>
                  <button onClick={() => delTemplate(t)} className="text-red-300/70 hover:text-red-300 p-1.5" title="Delete template" data-testid={`contract-template-delete-${t.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
            {templates.length === 0 && <p className="text-xs text-white/40 py-2">No templates yet.</p>}
          </div>
        </div>

        <div className="card-dark p-5" data-testid="contracts-send-card">
          <div className="font-display text-base text-white mb-1">Send for Signature</div>
          <p className="text-xs text-white/50 mb-4" style={{ fontFamily: "Inter, sans-serif" }}>Client gets an email + a "Review & Sign" card on their dashboard.</p>
          <div className="flex flex-col gap-2.5">
            <select className={inputCls} value={sendForm.template_id} onChange={(e) => setSendForm({ ...sendForm, template_id: e.target.value })} data-testid="contract-send-template">
              <option value="">Select contract…</option>
              {templates.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </select>
            <select className={inputCls} value={sendForm.user_id} onChange={(e) => setSendForm({ ...sendForm, user_id: e.target.value })} data-testid="contract-send-user">
              <option value="">Select client…</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name || u.email} ({u.email})</option>)}
            </select>
            <input className={inputCls} placeholder="Note to client (optional)" value={sendForm.note}
              onChange={(e) => setSendForm({ ...sendForm, note: e.target.value })} data-testid="contract-send-note" />
            <button onClick={send} disabled={busy} className="btn-gold !py-2 !px-4 !text-[11px] self-start disabled:opacity-60" data-testid="contract-send-btn">
              <FileSignature className="w-3.5 h-3.5" /> Send for Signature
            </button>
          </div>
        </div>
      </div>

      <div className="card-dark p-5" data-testid="contracts-requests-card">
        <div className="font-display text-base text-white mb-3">Signature Requests</div>
        <div className="flex flex-col gap-2">
          {requests.map((r) => (
            <div key={r.id} className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-2.5" data-testid={`contract-request-${r.id}`}>
              <div className="min-w-0">
                <div className="text-sm text-white" style={{ fontFamily: "Inter, sans-serif" }}>
                  {r.template_name} → <strong>{r.user_name || r.user_email}</strong>
                </div>
                <div className="text-[11px] text-white/40">Sent {String(r.sent_at).slice(0, 10)}{r.signed_at ? ` · Signed ${String(r.signed_at).slice(0, 10)}` : ""}</div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <span className={`px-2.5 py-1 rounded-full text-[10px] uppercase tracking-wider font-semibold ${statusCls(r.status)}`} data-testid={`contract-status-${r.id}`}>{r.status}</span>
                {r.status === "signed" && r.signed_doc_id && (
                  <button onClick={() => downloadSigned(r)} className="btn-outline !py-1.5 !px-3 !text-[10px]" data-testid={`contract-download-${r.id}`}>
                    <Download className="w-3 h-3" /> Signed PDF
                  </button>
                )}
                {r.status === "pending" && (
                  <button onClick={() => voidReq(r)} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full border border-red-500/40 text-red-300 text-[10px] hover:bg-red-500/10" data-testid={`contract-void-${r.id}`}>
                    Void
                  </button>
                )}
              </div>
            </div>
          ))}
          {requests.length === 0 && <p className="text-xs text-white/40 py-2">No signature requests yet.</p>}
        </div>
      </div>
    </div>
  );
}

function MarketplaceTab() {
  const CATEGORY_FALLBACK = [];
  const EMPTY = { name: "", category: "personal_loans", description: "", cta_type: "lead", url: "", active: true };
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState(CATEGORY_FALLBACK);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // partner id being edited, or "new"
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const inputCls = "w-full bg-black/30 border border-white/15 focus:border-[var(--if-gold)]/60 rounded-lg px-3 py-2.5 text-sm text-white placeholder:text-white/35 outline-none transition-colors";

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/marketplace");
      setItems(data.items || []);
      setCategories(data.categories || CATEGORY_FALLBACK);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); }, []);

  const catLabel = (id) => categories.find((c) => c.id === id)?.label || id;

  const startNew = () => { setForm(EMPTY); setEditing("new"); };
  const startEdit = (p) => {
    setForm({ name: p.name, category: p.category, description: p.description || "", cta_type: p.cta_type, url: p.url || "", active: p.active });
    setEditing(p.id);
  };

  const save = async () => {
    if (!form.name.trim()) { alert("Partner name is required."); return; }
    if (form.cta_type === "link" && !/^https?:\/\//.test(form.url.trim())) { alert("Direct-link partners need a valid http(s) affiliate URL."); return; }
    setBusy(true);
    try {
      if (editing === "new") await api.post("/admin/marketplace", form);
      else await api.patch(`/admin/marketplace/${editing}`, form);
      setEditing(null);
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Save failed."); }
    finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/marketplace/${p.id}`, { name: p.name, category: p.category, description: p.description || "", cta_type: p.cta_type, url: p.url || "", active: !p.active });
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Update failed."); }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete partner "${p.name}"? Click/lead stats will be lost.`)) return;
    try {
      await api.delete(`/admin/marketplace/${p.id}`);
      await reload();
    } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
  };

  const formFields = (
    <div className="grid sm:grid-cols-2 gap-3 mt-3" data-testid="admin-marketplace-form">
      <input className={inputCls} placeholder="Partner name *" value={form.name}
        onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="admin-marketplace-name" />
      <select className={inputCls} value={form.category}
        onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="admin-marketplace-category">
        {categories.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
      </select>
      <textarea className={`${inputCls} sm:col-span-2`} rows={2} placeholder="Short description shown on the public card"
        value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="admin-marketplace-description" />
      <select className={inputCls} value={form.cta_type}
        onChange={(e) => setForm({ ...form, cta_type: e.target.value })} data-testid="admin-marketplace-cta-type">
        <option value="lead">Request Intro (lead capture → you get notified)</option>
        <option value="link">Direct Link (your affiliate URL)</option>
      </select>
      <input className={inputCls} placeholder="Affiliate URL (https://…)" value={form.url}
        disabled={form.cta_type !== "link"}
        onChange={(e) => setForm({ ...form, url: e.target.value })} data-testid="admin-marketplace-url" />
      <label className="flex items-center gap-2 text-sm text-white/70" style={{ fontFamily: "Inter, sans-serif" }}>
        <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="admin-marketplace-active" />
        Visible on public marketplace
      </label>
      <div className="flex gap-2 justify-end">
        <button onClick={() => setEditing(null)} className="btn-outline !py-2 !px-4 !text-[11px]" data-testid="admin-marketplace-cancel">Cancel</button>
        <button onClick={save} disabled={busy} className="btn-gold !py-2 !px-4 !text-[11px] disabled:opacity-60" data-testid="admin-marketplace-save">
          {busy ? "Saving…" : "Save Partner"}
        </button>
      </div>
    </div>
  );

  return (
    <div className="flex flex-col gap-3">
      <div className="card-dark p-4" data-testid="admin-marketplace-header">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="font-display text-base text-white">Referral Partners</div>
            <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
              Manage the public <a href="/marketplace" target="_blank" rel="noreferrer" className="text-[var(--if-gold)] underline">/marketplace</a> page. Clicks & intro requests are tracked per partner.
            </div>
          </div>
          <button onClick={startNew} className="btn-gold !py-2 !px-4 !text-[11px]" data-testid="admin-marketplace-add-btn">
            <Plus className="w-3.5 h-3.5" /> Add Partner
          </button>
        </div>
        {editing === "new" && formFields}
      </div>

      {loading && <p className="text-sm text-white/50 text-center py-6">Loading…</p>}
      {items.map((p) => (
        <div key={p.id} className="card-dark p-4" data-testid={`admin-marketplace-partner-${p.id}`}>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-display text-base text-white">{p.name}</span>
                <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/8 text-white/60" style={{ fontFamily: "Inter, sans-serif" }}>{catLabel(p.category)}</span>
                <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${p.cta_type === "link" ? "bg-blue-500/15 text-blue-300" : "bg-[var(--if-gold)]/15 text-[var(--if-gold)]"}`} style={{ fontFamily: "Inter, sans-serif" }}>
                  {p.cta_type === "link" ? "Direct link" : "Intro request"}
                </span>
                {!p.active && <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-red-500/15 text-red-300" style={{ fontFamily: "Inter, sans-serif" }}>Hidden</span>}
              </div>
              <div className="text-xs text-white/50 mt-1 truncate max-w-xl" style={{ fontFamily: "Inter, sans-serif" }}>{p.description || "—"}</div>
              <div className="text-xs text-white/45 mt-1.5 flex items-center gap-3" style={{ fontFamily: "Inter, sans-serif" }}>
                <span data-testid={`admin-marketplace-clicks-${p.id}`}><strong className="text-white/80">{p.clicks ?? 0}</strong> clicks</span>
                <span data-testid={`admin-marketplace-leads-${p.id}`}><strong className="text-white/80">{p.leads ?? 0}</strong> intro requests</span>
                {p.url && <a href={p.url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[var(--if-gold)]"><ExternalLink className="w-3 h-3" />link</a>}
              </div>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <button onClick={() => toggleActive(p)} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid={`admin-marketplace-toggle-${p.id}`}>
                {p.active ? "Hide" : "Show"}
              </button>
              <button onClick={() => startEdit(p)} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid={`admin-marketplace-edit-${p.id}`}>
                <Edit className="w-3.5 h-3.5" /> Edit
              </button>
              <button onClick={() => remove(p)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all" data-testid={`admin-marketplace-delete-${p.id}`}>
                <Trash2 className="w-3.5 h-3.5" /> Delete
              </button>
            </div>
          </div>
          {editing === p.id && formFields}
        </div>
      ))}
      {!loading && items.length === 0 && <p className="text-sm text-white/50 text-center py-8">No partners yet — click "Add Partner".</p>}
    </div>
  );
}

function MailQueueTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/disputes/mail-queue");
      setItems(data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const downloadPDF = async (d) => {
    const { data } = await api.get(`/disputes/${d.id}/pdf`);
    window.open(data.url, "_blank", "noopener,noreferrer");
  };

  const markMailed = async (d) => {
    const today = new Date().toISOString().slice(0, 10);
    setBusyId(d.id);
    try {
      await api.post(`/admin/disputes/${d.id}/mark-mailed`, { sent_date: today });
      await load();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't mark as mailed.");
    } finally {
      setBusyId(null);
    }
  };

  // Group by payment_session_id so admins see the 3-bureau bundle together
  const groups = items.reduce((acc, d) => {
    const key = d.payment_session_id || d.id;
    (acc[key] = acc[key] || []).push(d);
    return acc;
  }, {});

  return (
    <div className="flex flex-col gap-4">
      {loading && items.length === 0 && <p className="text-sm text-white/50 text-center py-8">Loading…</p>}
      {!loading && items.length === 0 && <p className="text-sm text-white/50 text-center py-8">Mail queue empty — no premium-mail letters waiting.</p>}
      {Object.entries(groups).map(([sid, ds]) => (
        <div key={sid} className="card-dark p-5" data-testid={`mail-queue-group-${sid.slice(-8)}`}>
          <div className="flex items-center justify-between gap-3 mb-3">
            <div>
              <div className="font-display text-base text-white">{ds[0].user_name} <span className="text-white/40 text-sm">· {ds[0].user_email}</span></div>
              <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
                {ds[0].creditor}{ds[0].account_last4 ? ` · ••${ds[0].account_last4}` : ""}
                {ds[0].amount_usd != null ? ` · $${ds[0].amount_usd.toLocaleString()}` : ""}
              </div>
              <div className="text-xs text-white/45 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                Paid {new Date(ds[0].created_at).toLocaleDateString()} · {ds.length} letter{ds.length !== 1 ? "s" : ""} pending
              </div>
            </div>
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            {ds.map((d) => (
              <div key={d.id} className="p-3 rounded-lg border border-white/10 bg-black/20" data-testid={`mail-queue-${d.id}`}>
                <div className="font-mono-label text-[var(--if-gold)] mb-2">{d.bureau}</div>
                <div className="flex flex-col gap-2">
                  <button onClick={() => downloadPDF(d)} className="btn-outline !py-1.5 !px-3 !text-[10px] justify-center" data-testid={`mail-queue-pdf-${d.id}`}>
                    <Download className="w-3 h-3" /> Download PDF
                  </button>
                  <button
                    onClick={() => markMailed(d)}
                    disabled={busyId === d.id}
                    className="btn-gold !py-1.5 !px-3 !text-[10px] justify-center disabled:opacity-60"
                    data-testid={`mail-queue-mark-${d.id}`}
                  >
                    <Check className="w-3 h-3" /> {busyId === d.id ? "…" : "Mark Mailed"}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function DisputesTab() {
  const p = usePagination("/admin/disputes", 25);
  const [advancingId, setAdvancingId] = useState(null);

  const advance = async (d, status, extras = {}) => {
    setAdvancingId(d.id);
    try {
      await api.patch(`/disputes/${d.id}`, { status, ...extras });
      await p.reload();
    } catch (e) {
      alert(e?.response?.data?.detail || "Couldn't advance status.");
    } finally {
      setAdvancingId(null);
    }
  };

  const downloadPDF = async (d) => {
    try {
      const { data } = await api.get(`/disputes/${d.id}/pdf`);
      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch {
      alert("Couldn't open PDF.");
    }
  };

  return (
    <div className="flex flex-col gap-3">
      {p.items.map((d) => {
        const meta = DISPUTE_STATUS_META[d.status] || DISPUTE_STATUS_META.drafted;
        const StatusIcon = meta.icon;
        return (
          <div key={d.id} className="card-dark p-5" data-testid={`admin-dispute-${d.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full ${meta.cls}`}>
                    <StatusIcon className="w-3 h-3" />
                    {meta.label}
                  </span>
                  <span className="text-[10px] text-white/45 uppercase tracking-wider" style={{ fontFamily: "Inter, sans-serif" }}>
                    {d.bureau}
                  </span>
                </div>
                <div className="font-display text-base text-white">{d.creditor}</div>
                <div className="text-xs text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
                  {d.user_name} · {d.user_email}
                  {d.account_last4 ? ` · ••${d.account_last4}` : ""}
                  {d.amount_usd != null ? ` · $${d.amount_usd.toLocaleString()}` : ""}
                </div>
                <div className="text-xs text-white/45 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                  Drafted {new Date(d.created_at).toLocaleDateString()}
                  {d.sent_date ? ` · Sent ${d.sent_date}` : ""}
                  {d.tracking_number ? ` · USPS ${d.tracking_number}` : ""}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 shrink-0">
                <button onClick={() => downloadPDF(d)} className="btn-outline !py-2 !px-3 !text-[11px]" data-testid={`admin-dispute-pdf-${d.id}`}>
                  <Download className="w-3.5 h-3.5" /> PDF
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm(`Delete dispute for ${d.creditor} (${d.bureau})? Removes the PDF from Cloudinary too.`)) return;
                    try {
                      await api.delete(`/admin/disputes/${d.id}`);
                      await p.reload();
                    } catch (e) { alert(e?.response?.data?.detail || "Delete failed."); }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full border border-red-500/40 text-red-300 text-[11px] hover:bg-red-500/10 transition-all"
                  data-testid={`admin-dispute-delete-${d.id}`}
                >
                  <Trash2 className="w-3.5 h-3.5" /> Delete
                </button>
                {d.status === "sent" && (
                  <button
                    onClick={() => advance(d, "acknowledged", { bureau_response_date: new Date().toISOString().slice(0, 10) })}
                    disabled={advancingId === d.id}
                    className="btn-outline !py-2 !px-3 !text-[11px]"
                    data-testid={`admin-dispute-ack-${d.id}`}
                  >
                    <MailCheck className="w-3.5 h-3.5" /> Acknowledged
                  </button>
                )}
                {d.status === "acknowledged" && (
                  <button
                    onClick={() => advance(d, "investigating")}
                    disabled={advancingId === d.id}
                    className="btn-outline !py-2 !px-3 !text-[11px]"
                    data-testid={`admin-dispute-investigate-${d.id}`}
                  >
                    <Search className="w-3.5 h-3.5" /> Investigating
                  </button>
                )}
                {(d.status === "investigating" || d.status === "acknowledged" || d.status === "sent") && (
                  <select
                    onChange={(e) => {
                      if (!e.target.value) return;
                      advance(d, "resolved", { outcome: e.target.value, notes: `Outcome: ${e.target.value}` });
                      e.target.value = "";
                    }}
                    className="bg-white/5 border border-white/10 rounded-full px-3 py-1.5 text-xs text-white/80 focus:border-[var(--if-gold)]/40 outline-none"
                    data-testid={`admin-dispute-resolve-${d.id}`}
                  >
                    <option value="">Resolve as…</option>
                    <option value="deleted" className="bg-white">Deleted from report</option>
                    <option value="updated" className="bg-white">Updated / corrected</option>
                    <option value="verified" className="bg-white">Verified by bureau</option>
                    <option value="no_response" className="bg-white">No response (FCRA violation)</option>
                  </select>
                )}
              </div>
            </div>
            {d.outcome && (
              <div className="text-xs mt-2" style={{ fontFamily: "Inter, sans-serif" }}>
                <span className="text-white/50">Outcome:</span>{" "}
                <span className="text-green-300 capitalize">{d.outcome.replace(/_/g, " ")}</span>
              </div>
            )}
          </div>
        );
      })}
      {p.items.length === 0 && !p.loading && <p className="text-sm text-white/50 text-center py-8">No disputes filed yet.</p>}
      <PageControls {...p} label="disputes" />
    </div>
  );
}

function MrrSparkline({ data }) {
  if (!data || data.length === 0) return null;
  const W = 600, H = 90, PAD_X = 20, PAD_Y = 16;
  const max = Math.max(...data.map((d) => d.mrr), 1);
  const stepX = (W - PAD_X * 2) / Math.max(data.length - 1, 1);
  const points = data.map((d, i) => {
    const x = PAD_X + stepX * i;
    const y = H - PAD_Y - ((d.mrr / max) * (H - PAD_Y * 2));
    return { x, y, ...d };
  });
  const path = points.map((p, i) => (i === 0 ? `M${p.x},${p.y}` : `L${p.x},${p.y}`)).join(" ");
  const fill = `${path} L${points[points.length - 1].x},${H - PAD_Y} L${points[0].x},${H - PAD_Y} Z`;
  return (
    <div className="mt-1 mb-5" data-testid="mrr-sparkline">
      <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-[80px]" preserveAspectRatio="none">
        <defs>
          <linearGradient id="mrr-grad" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0%" stopColor="#4F5D1E" stopOpacity="0.35" />
            <stop offset="100%" stopColor="#4F5D1E" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fill} fill="url(#mrr-grad)" />
        <path d={path} fill="none" stroke="#4F5D1E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        {points.map((p) => (
          <circle key={`${p.year}-${p.month}`} cx={p.x} cy={p.y} r="3" fill="#0d0e0c" stroke="#4F5D1E" strokeWidth="1.5" />
        ))}
      </svg>
      <div className="flex justify-between mt-1 px-[20px] text-[10px] text-white/40" style={{ fontFamily: "Inter, sans-serif" }}>
        {data.map((d) => (
          <span key={`${d.year}-${d.month}`}>{d.label}</span>
        ))}
      </div>
    </div>
  );
}

function RevenueOverview() {
  const [m, setM] = useState(null);
  const [syncing, setSyncing] = useState(false);
  useEffect(() => {
    api.get("/admin/subscriptions/metrics").then(({ data }) => setM(data)).catch(() => setM(null));
  }, []);

  const sync = async () => {
    if (!window.confirm("Pull every active subscription from Stripe and apply to your customer records? Safe to re-run.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/subscriptions/admin/sync-from-stripe");
      let msg = `Synced ${data.synced_count} subscriber${data.synced_count === 1 ? "" : "s"}.`;
      if (data.not_found_count) {
        msg += ` ${data.not_found_count} Stripe customer${data.not_found_count === 1 ? "" : "s"} had no matching user (likely paid via Stripe link without a website signup).`;
      }
      if (data.error_count) {
        const firstErr = data.errors?.[0]?.error || "see logs";
        msg += `\n\n⚠️ ${data.error_count} error${data.error_count === 1 ? "" : "s"} during sync:\n${firstErr}`;
      }
      alert(msg);
      // refresh
      const { data: m2 } = await api.get("/admin/subscriptions/metrics");
      setM(m2);
    } catch (e) {
      alert(e?.response?.data?.detail || "Sync failed.");
    } finally { setSyncing(false); }
  };

  const mrr = m?.mrr ?? 0;
  const arr = m?.arr ?? 0;
  const customMonth = m?.custom_charges?.this_month ?? 0;
  const customMonthCount = m?.custom_charges?.this_month_count ?? 0;
  const totalMonth = mrr + customMonth;

  return (
    <div className="card-dark p-6 mb-6 border-[var(--if-gold)]/30 relative overflow-hidden" data-testid="revenue-overview">
      <div className="absolute -top-16 -right-16 w-56 h-56 bg-[var(--if-gold)]/8 rounded-full blur-3xl pointer-events-none" />
      <div className="relative">
        <div className="flex items-center justify-between gap-3 mb-1">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-[var(--if-gold)]" />
            <span className="font-mono-label text-[var(--if-gold)]">Recurring Revenue</span>
          </div>
          <div className="flex items-center gap-3">
            {m?.last_stripe_sync_at && (
              <span className="text-[10px] text-white/35" style={{ fontFamily: "Inter, sans-serif" }} title={`Auto-synced at ${new Date(m.last_stripe_sync_at).toLocaleString()}`}>
                Last sync: {_relativeTime(m.last_stripe_sync_at)}
              </span>
            )}
            <button
              onClick={sync}
              disabled={syncing}
              className="text-[10px] uppercase tracking-wider text-white/55 hover:text-[var(--if-gold)] underline-offset-2 hover:underline disabled:opacity-40"
              title="Reconcile with Stripe — fixes any subscribers that aren't showing"
              data-testid="sync-from-stripe-btn"
            >
              {syncing ? "Syncing…" : "↻ Sync from Stripe"}
            </button>
          </div>
        </div>
        <div className="flex flex-wrap items-baseline gap-3 mb-2">
          <span className="font-display text-5xl text-white" data-testid="revenue-mrr">${mrr.toFixed(2)}</span>
          <span className="text-sm text-white/55" style={{ fontFamily: "Inter, sans-serif" }}>
            MRR · <span className="text-white/75">${arr.toFixed(0)} ARR</span>
          </span>
          {customMonth > 0 && (
            <span className="text-xs text-white/45 ml-auto" style={{ fontFamily: "Inter, sans-serif" }}>
              + <span className="text-[var(--if-gold)]">${customMonth.toFixed(2)}</span> one-off this month →{" "}
              <span className="text-white" data-testid="revenue-total-month">${totalMonth.toFixed(2)}</span> total
            </span>
          )}
        </div>

        <MrrSparkline data={m?.trend_6mo} />

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>Active Clients</div>
            <div className="font-display text-2xl text-white" data-testid="revenue-active">{m?.active_clients ?? m?.active_subscribers ?? "–"}</div>
            {(m?.one_off_only_clients ?? 0) > 0 && (
              <div className="text-[10px] text-white/40 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>
                {m.active_subscribers} subscribers + {m.one_off_only_clients} one-off
              </div>
            )}
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>Trialing</div>
            <div className="font-display text-2xl text-white" data-testid="revenue-trialing">{m?.trialing ?? "–"}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1 flex items-center gap-1" style={{ fontFamily: "Inter, sans-serif" }}>
              <TrendingUp className="w-3 h-3 text-green-400" /> New this month
            </div>
            <div className="font-display text-2xl text-white" data-testid="revenue-new">{m?.new_this_month ?? "–"}</div>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3">
            <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1 flex items-center gap-1" style={{ fontFamily: "Inter, sans-serif" }}>
              <AlertTriangle className="w-3 h-3 text-amber-400" /> Pending churn
            </div>
            <div className="font-display text-2xl text-white" data-testid="revenue-churn">{m?.pending_churn ?? "–"}</div>
          </div>
        </div>

        {/* One-off / Custom charges block — separate from recurring MRR so the math stays clean */}
        <div className="bg-[var(--if-gold)]/5 border border-[var(--if-gold)]/20 rounded-xl px-4 py-4 mb-6" data-testid="custom-revenue-block">
          <div className="flex items-baseline justify-between gap-3 flex-wrap mb-3">
            <div className="flex items-center gap-2">
              <DollarSign className="w-4 h-4 text-[var(--if-gold)]" />
              <span className="font-mono-label text-[var(--if-gold)]">One-Off Charges</span>
            </div>
            <span className="text-[11px] text-white/45" style={{ fontFamily: "Inter, sans-serif" }}>
              Custom payments you&apos;ve taken directly from clients (not subscriptions)
            </span>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>This month</div>
              <div className="font-display text-3xl text-white" data-testid="custom-charges-month">${customMonth.toFixed(2)}</div>
              <div className="text-[11px] text-white/45 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>{customMonthCount} {customMonthCount === 1 ? "charge" : "charges"}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider text-white/45 mb-1" style={{ fontFamily: "Inter, sans-serif" }}>Lifetime</div>
              <div className="font-display text-3xl text-white" data-testid="custom-charges-lifetime">${(m?.custom_charges?.lifetime ?? 0).toFixed(2)}</div>
              <div className="text-[11px] text-white/45 mt-0.5" style={{ fontFamily: "Inter, sans-serif" }}>{m?.custom_charges?.lifetime_count ?? 0} total</div>
            </div>
          </div>
          {(m?.custom_charges?.recent?.length || 0) > 0 && (
            <div className="mt-4 pt-3 border-t border-[var(--if-gold)]/15">
              <div className="font-mono-label text-white/50 mb-2 text-[10px]">Recent</div>
              <div className="flex flex-col divide-y divide-white/5" data-testid="custom-charges-recent">
                {m.custom_charges.recent.map((p, i) => (
                  <div key={i} className="flex justify-between items-center py-1.5 text-xs" style={{ fontFamily: "Inter, sans-serif" }}>
                    <div className="min-w-0">
                      <div className="text-white truncate">{p.description}</div>
                      <div className="text-[10px] text-white/40 truncate">{p.user_email}</div>
                    </div>
                    <div className="text-[var(--if-gold)] shrink-0 ml-3">${p.amount.toFixed(2)}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {(m?.by_tier?.length || 0) > 0 && (
          <div className="mb-5">
            <div className="font-mono-label text-white/50 mb-2">By tier</div>
            <div className="flex flex-wrap gap-2">
              {m.by_tier.map((t) => (
                <div key={t.package_id} className="bg-[var(--if-gold)]/10 border border-[var(--if-gold)]/25 rounded-full px-4 py-1.5 text-xs flex items-center gap-2" style={{ fontFamily: "Inter, sans-serif" }}>
                  <span className="text-white/80">{t.name}</span>
                  <span className="text-white/40">·</span>
                  <span className="text-white"><strong>{t.count}</strong> subs</span>
                  <span className="text-white/40">·</span>
                  <span className="text-[var(--if-gold)]">${t.mrr.toFixed(2)}/mo</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(m?.recent_subscribers?.length || 0) > 0 && (
          <div>
            <div className="font-mono-label text-white/50 mb-2">Latest subscribers</div>
            <div className="flex flex-col divide-y divide-white/5">
              {m.recent_subscribers.map((s) => (
                <div key={s.id} className="flex justify-between items-center py-2 text-sm" style={{ fontFamily: "Inter, sans-serif" }}>
                  <div className="min-w-0">
                    <div className="text-white truncate">{s.name || s.email}</div>
                    <div className="text-[11px] text-white/40">{s.subscription_package_name}</div>
                  </div>
                  <div className="text-right shrink-0 ml-3">
                    <div className="text-[var(--if-gold)]">${(s.subscription_amount || 0).toFixed(2)}/mo</div>
                    <div className="text-[10px] text-white/40 capitalize">{s.subscription_status}{s.subscription_cancel_at_period_end ? " · cancelling" : ""}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {m && m.active_subscribers === 0 && m.trialing === 0 && (
          <p className="text-sm text-white/50 text-center py-4" style={{ fontFamily: "Inter, sans-serif" }}>
            No active subscribers yet. Once customers subscribe to Starter or Pro, their MRR will show up here in real time.
          </p>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);

  useEffect(() => {
    if (tab === "overview") api.get("/admin/overview").then(({ data }) => setOverview(data));
  }, [tab]);

  if (!user || user.role !== "admin") {
    return (
      <section className="section dark-zone text-center">
        <p className="text-white/60">Admin access required.</p>
      </section>
    );
  }

  return (
    <section className="section dark-zone">
      <div className="container-if">
        <div className="flex items-center gap-2 mb-2">
          <Shield className="w-4 h-4 text-[var(--if-gold)]" />
          <span className="font-mono-label text-[var(--if-gold)]">Command HQ</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl leading-tight mb-2">Admin Dashboard</h1>
        <p className="text-sm text-white/55 mb-8" style={{ fontFamily: "Inter, sans-serif" }}>
          Logged in as <strong className="text-white">{user.email}</strong>
        </p>

        <div className="flex gap-2 mb-8 overflow-x-auto">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-4 py-2.5 rounded-full border text-sm transition-all whitespace-nowrap ${
                tab === t.id ? "bg-[var(--if-gold)] text-[#F5F4EF] border-[var(--if-gold)] font-semibold" :
                "border-white/10 text-white/70 hover:border-[var(--if-gold)]/40"
              }`}
              data-testid={`admin-tab-${t.id}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === "overview" && (
          <>
            <RevenueOverview />
            <div className="grid sm:grid-cols-2 lg:grid-cols-6 gap-4">
              <StatCard label="Leads" value={overview?.leads ?? "–"} icon={Inbox} />
              <StatCard label="Bookings" value={overview?.bookings ?? "–"} icon={Calendar} />
              <StatCard label="Contacts" value={overview?.contacts ?? "–"} icon={Mail} />
              <StatCard label="Users" value={overview?.users ?? "–"} icon={Users} />
              <StatCard label="Paid tx" value={overview?.paid_transactions ?? "–"} icon={Check} />
              <StatCard label="Revenue" value={overview ? `$${overview.revenue_usd.toFixed(0)}` : "–"} icon={DollarSign} />
            </div>
            <div className="grid sm:grid-cols-2 gap-4 mt-4" data-testid="ebook-stats-row">
              <StatCard label="Ebook Buyers" value={overview?.ebook_buyers ?? "–"} icon={BookOpen} />
              <StatCard label="Ebook Revenue" value={overview ? `$${(overview.ebook_revenue_usd ?? 0).toFixed(2)}` : "–"} icon={DollarSign} />
            </div>
            {(overview?.source_breakdown || []).length > 0 && (
              <div className="card-dark p-6 mt-4" data-testid="source-breakdown-card">
                <div className="font-mono-label text-[var(--if-gold)] mb-4">Where Clients Come From</div>
                <div className="flex flex-col gap-2.5">
                  {(() => {
                    const rows = overview.source_breakdown;
                    const max = Math.max(...rows.map((r) => r.count), 1);
                    return rows.map((r) => (
                      <div key={r.source} className="flex items-center gap-3" data-testid={`source-row-${r.source.replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`}>
                        <span className={`w-40 shrink-0 text-xs truncate ${r.source === "Not set" ? "text-white/40 italic" : "text-white/75"}`} style={{ fontFamily: "Inter, sans-serif" }}>{r.source}</span>
                        <div className="flex-1 h-2.5 rounded-full bg-black/10 overflow-hidden">
                          <div className={`h-full rounded-full ${r.source === "Not set" ? "bg-[#23261C]/20" : "bg-[#4F5D1E]"}`} style={{ width: `${Math.max(4, (r.count / max) * 100)}%` }} />
                        </div>
                        <span className="w-8 text-right text-xs font-semibold text-white/80" style={{ fontFamily: "Inter, sans-serif" }}>{r.count}</span>
                      </div>
                    ));
                  })()}
                </div>
                <p className="text-[10px] text-white/40 mt-3" style={{ fontFamily: "Inter, sans-serif" }}>
                  Set each client's source in the Users tab — "Not set" shrinks as you tag them.
                </p>
              </div>
            )}
          </>
        )}
        {tab === "leads" && <LeadsTab />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "contacts" && <ContactsTab />}
        {tab === "payments" && <PaymentsTab />}
        {tab === "users" && <UsersTab />}
        {tab === "documents" && <DocumentsTab />}
        {tab === "disputes" && <DisputesTab />}
        {tab === "marketplace" && <MarketplaceTab />}
        {tab === "contracts" && <ContractsTab />}
        {tab === "blog" && <BlogTab />}
        {tab === "mail_queue" && <MailQueueTab />}
      </div>
    </section>
  );
}
